import { ITEMS, weaponStats, weaponFlightCost } from "@hyperlight/shared";
/**
 * On-foot character: planet-walker simulation + third-person orbit camera.
 *
 * Position is the FEET point in system space (float64 JS numbers, like the
 * ship). Ground collision clamps against the analytic terrain sampler — the
 * exact function the meshes are built from — so feet stand on what you see,
 * meshed or not. Local "up" is radial; the camera yaw frame is parallel-
 * transported across the sphere each tick (re-orthogonalized against the
 * drifting up vector) so walking around the planet never spins the view.
 *
 * Runs in the fixed 60 Hz simulation with (prevPos, prevQuat) render
 * interpolation, same as the ship.
 */
import * as THREE from "three";
import { resolveCharacterPose } from "@hyperlight/shared";
import { ENERGY_BASE, juggleLift, waterColumn, waterGravity, type CombatStats, type WaterColumn } from "@hyperlight/shared";
import type { Input } from "../engine/input";
import type { PlanetHost } from "../world/planetHost";
import { TerrainContacts } from '../world/terrainContacts';
import { DIVE_LANDING } from './diveLanding';
import {GLIDE,GlidePhysics} from './glidePhysics';
import {GlideApproach} from './glideApproach';
import {WallRunMotion,WALL_RUN} from './wallRun';
import {LedgeClimbMotion} from './ledgeClimb';
import {FLIGHT_LAUNCH_TIME,FLIGHT_LAUNCH_THRUST} from './flightLaunch';
import {FLIGHT_LAUNCH_TOTAL,FLIGHT_LAUNCH_BRAKE,flightLaunchRetention,FlightLaunchRecovery,DIVE_PREPARATION,divePreparationRetention,divePreparationTurn,diveFallSpeed,diveBrakeSpeed,DIVE_BRAKE,HOVER,hoverVerticalVelocity} from '../../../shared/src/flightPhysics';

const MOUSE_SENS = 0.0022;
const EYE_HEIGHT = 1.55;
const WALL_RADIUS = 0.45; // lateral body radius vs town building walls
/**
 * Longest single advance the body may make before solids get a say, metres.
 *
 * ⚠⚠ THIS IS WHY PLAYERS WALKED OUT THROUGH WALLS (measured 2026-07-31, reported from
 * cache play as "you slip out at corners"). Solid resolution is a PUSH-OUT, not a
 * sweep: it looks at where the body ENDED and shoves it to the cheapest exit, capped at
 * `MAX_PUSH_M` (0.6 m) per call. So a frame that moves the body further than that in
 * one go is unrecoverable — the push can never catch the motion, the body sinks a
 * little deeper every tick, and once its centre passes a wall's mid-plane the cheapest
 * exit is the FAR side. It is not a cache bug: town walls and homestead pieces have
 * always had it. It shows up in a cache because a cache is where the movement kit's
 * fast toys get used indoors — a 28 m/s stinger leap is 0.47 m/tick at 60 fps and 0.93
 * at 30, and a corner is the cheapest solid in the dungeon to cross because a diagonal
 * splits the step over two axes.
 *
 * The fix is to stop making that frame: advance in slices no longer than this and
 * resolve after each one. At a run (11 m/s) a 60 Hz tick is 0.18 m and nothing changes
 * — the cost is paid only by the motion that was broken.
 */
const SOLID_STEP_M = 0.3;
/** Slice cap, so a teleport-scale delta cannot turn one tick into a loop. */
const SOLID_STEP_MAX = 16;
const CROUCH_CAM_DROP = 0.5; // the eye sinks this far when crouching (matches the avatar's CROUCH_DROP)
// Move pace is tied to the animation set so strides land 1-to-1 (no foot-slide):
// default ground pace is a RUN (the run clip drives it); held Shift = SPRINT
// boost on top. The walk clip only covers the low end (decelerating toward stop),
// which the avatar selects by speed (see RUN_ANIM_MIN) — no walk SPEED target.
const RUN_SPEED = 5.5; // default ground movement (run clip authored ~5.9 m/s)
// Shift sprint. Kept near the sprint clip's authored pace (~6.2 m/s) so the legs
// play close to their natural cadence (timeScale ~1.1) instead of being warped
// fast — pushing this much higher makes the sprint legs cycle "too fast for the
// speed" (the clip just isn't authored for it).
const SPRINT_SPEED = 7.0;
// PARKED MOVEMENT FEATURES (2026-07-13): keep the implementations below intact
// for possible later tuning, but do not expose super sprint / super jump in the
// live movement set. Ordinary sprint and the normal jump chain remain on.
export const SUPER_SPRINT_ENABLED = false;
export const SUPER_JUMP_ENABLED = false;
// Boost-run: the top sprint gear. Hold Shift + move forward; after BOOST_RAMP of sustained sprint it
// jet-assists into a faster glide-run (camera pulls back, stride slows, fuel sips, a jump launches
// straight into glide). Speed is well above the sprint clip's authored pace on purpose — the stride
// is deliberately SLOWED (boost-glide look), so the foot-slide reads as "boosting", not a fast run.
const BOOST_SPEED = 11.0;
const BOOST_BURN = 3; // energy/s for the parked boost-run feature
const BOOST_RAMP = 2.5; // sustained forward-sprint (s) before boost engages (1s of run, then a 1.5s arc wind-up)
const BOOST_ARC_DELAY = 1.0; // sprint this long (s) before the wind-up arc shows up (like the jump's hold)
const BOOST_MIN_FUEL = 8; // need at least this much fuel to engage / keep boosting
const CROUCH_SPEED = 3.0; // brisk crouch-walk (was 1.4 — too slow); ~55% of RUN_SPEED
/** RMB-while-unarmed SLOW WALK pace (m/s) — the omnidirectional walk stance (NOT combat). Tunable;
 *  ~matches the M_Neutral walk-loop's authored stride so the feet don't slide. */
const WALK_MODE_SPEED = 2.2;
const COMBAT_SPEED = 0.95; // unarmed RMB stance FALLBACK walk (m/s); the live value is fed per-tick from
//                            avatar.combatGroundSpeed() into `combatSpeed` — see that for the lock-step
//                            walk / moving-strike lunge / in-place-strike root. Slow: locks to the footwork.
// EVADE_SPEED is the tangential dash impulse. The dash COASTS under GROUND_FRICTION over the i-frame
// (The old impulse-coast dodge constants — EVADE_SPEED 24 / ROLL_FRICTION 5.5 / ROLL_WINDOW —
// are RETIRED: the dodge is a UNIFORM-velocity roll now, DODGE_SPEED/DODGE_WINDOW below, and
// attack lunges keep their own impulse math in attackDash/attackLungeAt.)
const EVADE_IFRAMES = 0.18; // s of invulnerability from dash start (tunable)
const DASH_COOLDOWN = 0.5; // s of cooldown between evade-dashes (can't chain dashes back-to-back)
// attackDash lunges coast for this long (impulse mode — the rolls use DODGE_WINDOW instead).
const DASH_WINDOW = 0.4;
const GROUND_ACCEL = 28;
const AIR_ACCEL = 7;
// (The hold-to-charge air-attack hover/lunge constants were REMOVED with the plunge, 2026-07-21.)
/** Exponential tangential decay when grounded with no input. */
const GROUND_FRICTION = 11;
/** ROLL-dodge drive (the Roll family IS the dodge) — the Elden-Ring profile: FAST out of
 *  the gate, easing down across the window, and ending at a real walking pace rather than
 *  zero — the exit FLOWS into locomotion (movement input past the window also cancels the
 *  clip's recovery tail, main.ts). speed(k) = END + (START−END)·k², k = remaining/window →
 *  avg ≈ END + (START−END)/3 ≈ 5.6 m/s → ~4.0 m standing, ~5.0 m crouched. */
const DODGE_SPEED_START = 10;
const DODGE_SPEED_END = 3.4;
const DODGE_WINDOW = 0.72;
const CROUCH_ROLL_WINDOW = 0.9;
/** STINGER homing-dash speed (m/s) — the soft-lock gap closer. VERY fast by user call
 *  (2026-07-21): the close should read as a leap onto the target, not a run — 28 m/s puts
 *  a 10 m gap at ~0.36 s. The sd_dash clip (PS Combo C4 at the attack retime) rides it. */
const STINGER_SPEED = 28;
/** …easing down to this by arrival (user call: "slow down the jump towards the target"):
 *  the leap decelerates over the last STINGER_SLOW_R metres so the strike lands as a
 *  readable arrival, not a fly-by. */
const STINGER_SPEED_END = 8;
const STINGER_SLOW_R = 6;
/** The leap's vertical pop CAP (m/s) — the actual pop is sized from the predicted flight
 *  time (stingerSteer's flightS) so the arc TOUCHES DOWN just short of the target (user
 *  call: the leap "lands" on the ground near the enemy, never arrives hanging mid-air):
 *  v = g·T·0.45 → airborne for ~90% of the flight. The cap keeps long closes subtle. */
const STINGER_LEAP_V = 4.6;
/** Airborne dodge impulse (m/s) — the quick mid-air side-step (lockless 1H + non-melee). */
const AIR_DODGE_IMPULSE = 7;
/** AIR-EVADE ORBIT (1H + lock): circle the target for this long at ω·dist tangential speed
 *  (capped) while the vertical velocity damps to a hover — "horizontally in the air". */
const AIR_ORBIT_S = 0.5;
const AIR_ORBIT_RATE = 2.6; // rad/s around the target
const AIR_ORBIT_MAX = 10; // tangential speed cap (m/s)
/** How long an AIR-FOCUS feed stays good (s) — a couple of ticks; see setAirFocus. */
const AIR_FOCUS_HOLD = 0.2;
/** AIR-RAVE fall gravity fraction: while the aerial string is live and DESCENDING, gravity
 *  eases off so the pair hangs between hits (user call: "we fall too quickly in the air").
 *  Rising keeps FULL gravity — the High-Time apex must still match the enemy launch. */
const RAVE_FALL_G = 0.55;
/** The FIRST air-string hop (m/s). Further hops decay on the shared juggleLift ladder — see
 *  aerialHop; the victim's re-lift decays on the same curve, so the pair descends together. */
const AERIAL_HOP = 4.2;

// --- Suck-to-target melee lunge (attackLungeAt — see combat/targetAssist.ts) ---
/** Arrival point: the lunge stops this far (m) from the target's centre — inside blade
 *  reach, outside their model. */
const MELEE_STOP_DIST = 1.6;
/** Impulse→distance under the dash coast: d ≈ v0·(1−e^(−k·w))/k, k=GROUND_FRICTION,
 *  w=DASH_WINDOW. Divide a wanted distance by this to size the impulse. */
const LUNGE_COVER = (1 - Math.exp(-GROUND_FRICTION * DASH_WINDOW)) / GROUND_FRICTION;
/** Impulse cap (m/s) for the AIRBORNE lunge (the air rave keeps the impulse). */
const LUNGE_MAX = 34;
/** Most ground a single swing's STRIKE STEP covers (m). A step, never a rocket:
 *  wider gaps are the stinger's job (the locked gap-closer). */
const STRIKE_STEP_REACH = 2.2;
/** How fast momentum carried INTO a swing bleeds off (1/s): a running attack
 *  slides into the strike instead of stopping dead on the first frame. */
const STRIKE_CARRY_DECAY = 9;
/** Fastest a strike step may move (m/s): a long step stretches its duration
 *  (the smootherstep peak is 1.875·distance/duration) instead of lurching. */
const STRIKE_STEP_PEAK = 10;
const STRIKE_CARRY_MAX = 7;
/** Auto-face cap per swing (rad ≈ 29°): the heading (and so the coupled camera) NUDGES onto
 *  the target; a full snap read as camera hijack (the GoW lateral-suck lesson). */
const FACE_MAX = 0.5;


/** Pure decision: does the combat stance HARD-ROOT tangential velocity THIS tick? An in-place strike
 *  (combatSpeed→0) roots so a held strafe can't slide the fighter — BUT an active evade-dash must NOT
 *  be rooted (it has to coast and travel), so a live dash exempts the root. Mirrors selectLocomotion's
 *  pure-decision pattern; unit-tested in client/test/character.test.mts. */
export function isCombatRooted(combatStance: boolean, combatSpeed: number, dashing: boolean): boolean {
  return combatStance && combatSpeed <= 1e-3 && !dashing;
}
const GRAVITY = 16;
/** Surface gravity scales with body size — moons pull gently. */
function sizeG(radius: number): number {
  return waterGravity(radius) / GRAVITY;
}
const JUMP_SPEED = 7.5;
// Super-jump: hold Space (grounded) to wind up a bigger leap. A quick tap (< JUMP_HOLD) is the normal
// jump; past JUMP_HOLD a charge meter fills over JUMP_CHARGE seconds, and releasing pops up to
// JUMP_SPEED_MAX + briefly engages the jetpack hover (the Takeoff_02 flourish → vertical climb → hover).
const JUMP_SPEED_MAX = 28; // fully-charged launch speed — ~3× the height of the un-charged jump (apex ∝ v²)
const JUMP_HOLD = 0.5; // Space held this long (s) before the charge begins
const JUMP_CHARGE = 1.25; // charge ramp (s) from min → max after the hold threshold (snappy — half the old 2.5)
const SUPER_HOVER_GRACE = 1.1; // the super-jump hover persists this long without Space (so the release doesn't kill it)
// A super-jump / big-leap only converts to FLIGHT if the follow-up air-tap lands within this window after
// the launch. This belongs to the parked super-jump feature; ordinary triple-jump
// gliding does not require a narrow re-tap window.
const SUPER_FLIGHT_WINDOW = 1.1;
// BOOST BIG-LEAP: a boost-sprint jump is a giant forward-BOUNDING leap (NOT an instant glide). It launches
// ballistically — up + a forward bound carrying the boost momentum — and arcs back down (landing on the
// sprint-land clip) UNLESS a further air-tap converts it to flight (glide). Charging the jump (hold Space)
// scales BOTH the height and the distance. The leap keeps sipping boost fuel at BOOST_BURN the whole arc.
const BOOST_LEAP_UP = 9.5; // base vertical launch (m/s) — a touch above the normal jump
const BOOST_LEAP_UP_MAX = 34; // fully-charged vertical launch — 2× the prior max (17) for a towering charged leap
// Forward bound (m/s), ADDED to the boost run momentum. Tuned DOWN 50% (2026-06-25 — "doesn't fling so
// far") from the prior 45 / 95: ~33 m/s base, ~58 m/s fully charged (still a strong bound, half the reach).
const BOOST_LEAP_FWD = 22; // base forward bound impulse
const BOOST_LEAP_FWD_MAX = 47; // fully-charged forward bound
const COYOTE_TIME = 0.12;
/** Suit drag caps freefall — drops are survivable decisions, not coin flips. */
const TERMINAL_FALL = 52;

/** The JETPACK verbs — hover (tap 2, held), glide (tap 3), dive (X in the sky).
 *  PARKED 2026-07-21 when the DMC combat kit took over the air (tap 2 became a plain
 *  DOUBLE JUMP + UAL one-shot); RE-ENABLED 2026-08-03 — the triple-jump-to-fly and the
 *  X dive are wanted back. The DMC air kit still wins where it overlaps: actionAirLock
 *  roots the jump chain during an air action, and aiming is suppressed in glide/dive.
 *  (The sword air-attack charge hover is NOT a jet — it is unaffected either way.) */
export const JETPACK_ENABLED = true;

// --- Crystal flight: double jump stays ballistic; the third tap deploys flight. ---
// Coasting is free; hover spends a quarter of held Shift thrust from the ENERGY pool,
// scaled by the flight upgrade and armor. Recharge requires solid ground.
/** Delay after touch-down before the ground recharge kicks in. */
const FUEL_REGEN_DELAY = 0.35;
/** Seconds of recharge lockout after a COMBAT spend — long enough that sustained fire
 *  genuinely drains (the ground regen is fast, because flight is tuned around it). */
const COMBAT_REGEN_DELAY = 0.9;
/** Double jump gives a punchy pop, then follows ordinary jump gravity. */
const DOUBLE_JUMP_POP = 6.5;
const HOVER_SPEED = 7.5;
const HOVER_ACCEL = 16; // strong air control while hovering
/** Hovering ACTIVELY reclaims control: horizontal velocity eases toward the WISHED velocity (0 with no
 *  WASD) at this rate, so a fast entry into hover (a super-jump apex, breaking out of a dive, re-engaging
 *  after a fast fall) sheds its carried momentum in a fraction of a second instead of coasting off — the
 *  player gets to steer again almost immediately. Higher = snappier grab of control. */
const HOVER_CONTROL = 7;
// DIVE folds away lift: gravity builds downward speed, without engine thrust.
// WASD steers a lateral drift; releasing X carries the earned speed into glide.
const DIVE_DRIFT_SPEED = 14; // m/s lateral steering while diving (a bit over sprint — a real "drive")
const DIVE_CONTROL = 4; // lateral ease rate (1/s): toward the wish with WASD, back to vertical without
const DIVE_MIN_HEIGHT = 10; // feet must be at least this high above the surface (m) to START a dive
/** Look-driven dive has a dead band so slight mouse movement cannot flap poses. */
export const AIM_DIVE={enterPitch:68*Math.PI/180,exitPitch:58*Math.PI/180}as const;
/** Dive touchdown PAUSE: movement + jump root for this long while the SuperHero flight-land flourish
 *  plays — the slam is a beat, not something you instantly run out of. */
const DIVE_LAND_PAUSE = DIVE_LANDING.lock;
/** Glue-down range while walking, so downhill strides don't flicker airborne. */
const SNAP_DIST = 0.7;
/** Steeper than ~62° stops being walkable: no friction — you slide. */
const WALKABLE_COS = Math.cos((62 * Math.PI) / 180);
/** Slope sample arc (m): a stride, so detail noise doesn't read as cliffs. */
const SLOPE_EPS = 1.6;
// --- Water: wade in shallows, swim where it's too deep to stand ---
/** Deepest standing water (m of column under the feet). Beyond this you SWIM. */
const WADE_MAX = 1.4;
/** Walk-speed multiplier while wading (shallow water drags). */
const WADE_SLOW = 0.55;
/** Bank slack: the current scales from this fraction at the wet-channel edge up
 *  to full strength mid-channel (riverK 0→1) — slack water at the banks gives
 *  swimmers an escape route, and it's physical. Speed itself is per-point data
 *  from the river graph (WaterColumn.flowSpeed), not a constant. */
const CURRENT_BANK_SLACK = 0.3;
/** Swim locomotion. */
const SWIM_SPEED = 3.2;
const SWIM_SPRINT_SPEED = 5.8; // Shift while swimming → the faster front-crawl gear
const SWIM_ACCEL = 10;
const SWIM_DRAG = 3.2; // tangential + vertical damping while swimming
/** The floating waterline sits this far below the eye (head stays out). */
const SWIM_SUBMERGE = 1.25;
const BUOYANCY = 6; // spring strength toward the float line
const SWIM_VERT_ACCEL = 5; // dive (X/C) / surface (Space) input accel (m/s²)
const SWIM_VERT_MAX = 4; // max dive/surface speed (m/s)
const WATER_LEAP_SPEED = 9; // a jump tapped at the surface launches the swimmer this fast (clears ~1.5 m)
const WATER_LEAP_GRACE = 0.45; // forced-airborne window after a leap so buoyancy can't drag it back

export class CharacterController {
  /** Feet position, system space. */
  readonly pos = new THREE.Vector3();
  readonly prevPos = new THREE.Vector3();
  /** Visual orientation: up-aligned, facing the movement direction. */
  readonly quat = new THREE.Quaternion();
  readonly prevQuat = new THREE.Quaternion();
  readonly vel = new THREE.Vector3();
  /** Radial up at the character, refreshed every tick. */
  readonly up = new THREE.Vector3(0, 1, 0);
  /** Camera yaw direction, kept tangent to the sphere (parallel transport). */
  readonly heading = new THREE.Vector3(0, 0, -1);
  /** Camera pitch above the horizon, radians (positive looks down). */
  pitch = 0.3;
  /** Look-speed multiplier tracking the live camera zoom (main feeds fov/base
   *  each frame) — an ADS scope slows the wrist by its magnification. */
  lookScale = 1;
  onGround = false;
  /** Touching ground too steep to stand on (sliding) — jump still allowed. */
  sliding = false;
  /** Water interaction state, recomputed each tick from the deterministic sea/river fields. */
  waterState: "land" | "wade" | "swim" = "land";
  /** Eased 0..1 — how submerged the body is (anim, underwater camera, VFX, net all read this). */
  submergeK = 0;
  sprinting = false;
  /** Crouch (X toggle): kneeling stance, slower, can't sprint; auto-stands airborne. */
  crouching = false;
  /** Right-mouse aim: strafe-locked to the camera, weapon raised. */
  aiming = false;
  heldItemId: string | null = null;
  private weaponReleaseT=0;
  /** Stowed weapons restore their wing budget after the disassembly interval. */
  get weaponMobility():number {
    return this.heldItemId&&(this.aiming||this.weaponReleaseT>0)?weaponStats(ITEMS[this.heldItemId]?.weapon??{}).mobility:1;
  }
  poseState() {
    return resolveCharacterPose({ itemId: this.heldItemId, speed: this.groundSpeed,
      crouching: this.crouching, grounded: this.onGround, aimHeld: this.aiming, focused: this.strafeLock });
  }
  /** STRAFE-LOCK without the aim hold (user call 2026-07-22): face the camera heading —
   *  the decision layers set it while a STICKY TARGET LOCK is live or the 2H guard is up,
   *  so the body faces the enemy and movement strafes (the aiming-facing rule, minus the
   *  weapon-raise semantics `aiming` carries). Written every combat tick. */
  strafeLock = false;
  /** AIR-ACTION INPUT LOCK (user call 2026-07-22): while the down-thrust slam (gpound) is
   *  in flight, Space may not dodge/jump/jet — the dive owns the body until touchdown.
   *  Written every combat tick by the decision layers. */
  actionAirLock = false;
  /** RMB tap-vs-hold window (s) before the aim engages — the sword parry's discrimination
   *  (actionSystem PARRY_TAP_S; main sets it in melee mode, 0 everywhere else). */
  aimDelayS = 0;
  private rmbHeldT = 0;
  /** Hold the aim stance without RMB (homestead build mode: the over-the-shoulder
   *  camera + strafe-lock is the precision framing while placing pieces). */
  forceAim = false;
  /** Unarmed RMB fighting stance (set by main.ts) — moves at a slow, deliberate lock-step pace.
   *  NOTE: unarmed COMBAT is currently UNFINISHED/disabled (see UNARMED_COMBAT_ENABLED in main.ts), so
   *  this stays false in normal play; RMB+unarmed sets `walkMode` instead. */
  combatStance = false;
  /** RMB-while-unarmed SLOW WALK — computed from this tick's aim and sprint input, capped to WALK_MODE_SPEED,
   *  camera-locked (via `aiming`) so the omnidirectional walk strafes. No combat (no root, no dodge). */
  walkMode = false;
  /** Combat-stance target ground speed (m/s) for THIS tick — set by main.ts from
   *  avatar.combatGroundSpeed(): the lock-step walk shuffle, a moving-strike lunge, or 0 (an in-place
   *  strike roots the fighter, so a held strafe can't slide it). Defaults to the walk fallback. */
  combatSpeed = COMBAT_SPEED;
  /** Invulnerability window (s) after an evade-dash — main.ts skips incoming-hit self-apply while > 0. */
  iframeT = 0;
  /** Dash-coast (no combat-root) window (s) — the dodge travels for this long, beyond the i-frames. */
  dashT = 0;
  /** Evade-dash cooldown (s) — counts down after a dash; a new dash is blocked until it hits 0. */
  dashCooldownT = 0;
  /** Friction the active dash coasts under (lower = travels further) — set per-dash by evade(). */
  private dashFriction = GROUND_FRICTION;
  /** Directional dodge the controller decided this tick — main.ts plays the roll, then
   *  clears. 6-way: back-diagonal inputs pick the SSH Roll_BL/BR clips (user call). */
  pendingDodge: "f" | "b" | "l" | "r" | "bl" | "br" | null = null;
  /** AIRBORNE directional dodge this tick — main/lab play the UAL Dodge_L/R (avatar.airDodge)
   *  and clear. Mid-air there is NO roll (user call); side comes from the lateral input. */
  pendingAirDodge: "l" | "r" | null = null;
  /** Space+direction DODGES this tick even without the RMB focus hold — main.ts raises it while
   *  the sword combo is open (mid-fight), so escaping a chain never requires re-pressing RMB. */
  evadeReady = false;
  /** Set on the tick a DOUBLE JUMP fires — main/lab play the UAL
   *  DoubleJump one-shot (avatar.flagDoubleJump) and clear it. */
  doubleJumpPending = false;
  /** Evade-dash in the stance: a quick tangential impulse + an i-frame window + a (longer) travel window so
   *  the dodge slides across its whole roll. dir is heading-relative (fwd/right). `friction`/`window` tune the
   *  coast decay + travel time — the CROUCH ROLL passes a lower friction + longer window for ~2× distance. */
  evade(dir: { fwd: number; right: number }, speed = DODGE_SPEED_START, window = DODGE_WINDOW): void {
    if(this.diveLandingLocked)return;
    const f = dir.fwd, r = dir.right;
    const mag = Math.hypot(f, r);
    this.tmp.copy(this.facing).multiplyScalar(mag > 1e-3 ? f / mag : 1) // neutral → forward
      .addScaledVector(this.right, mag > 1e-3 ? r / mag : 0);
    this.tmp.normalize();
    // UNIFORM drive: SET the tangential velocity to the dodge line (keep the radial part) —
    // the dash branch then HOLDS this magnitude for the whole window, so the roll moves at
    // one speed start to finish instead of impulse-lurching and decaying. Writes `vel` (the
    // integrated velocity), not the vTan scratch — see the vel↔vTan recombine note above.
    const vUp = this.vel.dot(this.up);
    this.vel.copy(this.up).multiplyScalar(vUp).addScaledVector(this.tmp, speed);
    this.strikeStepActive = false; // a dodge overrides any swing's step
    this.iframeT = EVADE_IFRAMES;
    this.dashT = window;
    this.dashWindow = window;
    this.dashSpeed = speed;
    this.dashCooldownT = DASH_COOLDOWN; // block the next dash until this lapses (unchanged for the roll)
  }
  /** >0 = the live dash DRIVES the tangential speed on the Elden-Ring profile (see
   *  DODGE_SPEED_START); 0 = legacy impulse coast (attack lunges). */
  private dashSpeed = 0;
  private dashWindow = DODGE_WINDOW;
  /** Attack lunge (punch/kick movement profile): a tangential dash IMPULSE in a heading-relative
   *  direction + the coast window so the strike travels with its clip. No i-frames (attacks aren't
   *  invulnerable). Kicks root the base (combatSpeed→0) so only this carries them; punches keep their
   *  locomotion on top. Same vel (not vTan) reasoning as evade(). */
  attackDash(fwd: number, right: number, speed: number): void {
    if(this.diveLandingLocked)return;
    const mag = Math.hypot(fwd, right);
    this.tmp.copy(this.facing).multiplyScalar(mag > 1e-3 ? fwd / mag : 1) // neutral → forward
      .addScaledVector(this.right, mag > 1e-3 ? right / mag : 0);
    this.vel.addScaledVector(this.tmp, speed);
    this.dashT = DASH_WINDOW;
    this.dashSpeed = 0; // impulse coast, not the uniform roll drive
    this.strikeStepActive = false;
  }
  /** STRIKE STEP — a swing's authored travel. The body accelerates through the
   *  windup, is fastest at CONTACT (`contactS` after the swing starts) and settles
   *  by twice that: distance follows a smootherstep, so the step never starts or
   *  stops with a jolt. Speed carried in bleeds off on top of it. Replaces the old
   *  one-frame impulse that shoved the body before the blade had even wound up. */
  attackStep(dir: THREE.Vector3, distance: number, contactS: number): void {
    if (this.diveLandingLocked) return;
    this.strikeStepDir.copy(dir).addScaledVector(this.up, -dir.dot(this.up));
    if (this.strikeStepDir.lengthSq() < 1e-8) this.strikeStepDir.copy(this.facing);
    this.strikeStepDir.normalize();
    this.strikeStepDist = Math.max(0, Math.min(STRIKE_STEP_REACH, distance));
    this.strikeStepDur = Math.min(.6, Math.max(.22, 2 * contactS, 1.875 * this.strikeStepDist / STRIKE_STEP_PEAK));
    this.strikeStepT = 0;
    this.strikeStepCarry = Math.min(STRIKE_CARRY_MAX, Math.max(0, this.vel.dot(this.strikeStepDir)));
    this.strikeStepActive = true;
    this.dashT = this.strikeStepDur;
    this.dashSpeed = 0;
  }
  private readonly strikeStepDir = new THREE.Vector3();
  private strikeStepDist = 0;
  private strikeStepDur = 0;
  private strikeStepT = 0;
  private strikeStepCarry = 0;
  private strikeStepActive = false;
  /** Input-relative strike step for a swing with no target (heading frame). */
  attackStepLocal(fwd: number, right: number, distance: number, contactS: number): void {
    const mag = Math.hypot(fwd, right);
    this.tmp.copy(this.facing).multiplyScalar(mag > 1e-3 ? fwd / mag : 1).addScaledVector(this.right, mag > 1e-3 ? right / mag : 0);
    this.attackStep(this.tmp, distance, contactS);
  }

  /** SUCK-TO-TARGET lunge (GoW recipe — see combat/targetAssist.ts): auto-FACE the target
   *  (heading rotation CLAMPED so the coupled camera reads a nudge, not a hijack) and dash
   *  sized to ARRIVE ~MELEE_STOP_DIST short of it under the normal dash-coast physics — a
   *  swing near an enemy lands ON that enemy instead of whiffing at air. `targetWorld` is
   *  the target's system-space position, `dist` the tangent-plane distance the picker
   *  measured. Callers fall back to attackDash() when no target was picked. */
  attackLungeAt(targetWorld: THREE.Vector3, dist: number, contactS = 0.16): void {
    if (!this.faceTarget(targetWorld)) return;
    // On the ground the approach is the swing's own STRIKE STEP, sized to arrive.
    // GoW's lesson for a close camera: a target off to the side reads as much
    // further away, so the pull shrinks with its angle from where you look.
    if (this.onGround) {
      const off = Math.acos(Math.max(-1, Math.min(1, this._faceHeading.dot(this._lungeDir))));
      const reach = STRIKE_STEP_REACH * (1 - .6 * Math.min(1, off / (Math.PI / 2)));
      this.attackStep(this._lungeDir, Math.min(reach, dist - MELEE_STOP_DIST), contactS);
      return;
    }
    // The air rave keeps its impulse lunge.
    const v0 = Math.min(LUNGE_MAX, Math.max(0, dist - MELEE_STOP_DIST) / LUNGE_COVER);
    if (v0 > 0.5) {
      this.vel.addScaledVector(this._lungeDir, v0 - this.vel.dot(this._lungeDir));
      this.dashT = DASH_WINDOW;
    }
  }
  private readonly _faceHeading = new THREE.Vector3();
  /** Auto-face a target for a strike (GoW): the body turns fully onto it, the
   *  camera-owning heading only nudges (FACE_MAX). Leaves `_lungeDir` pointing
   *  at it on the tangent plane; false when the target is on top of us. */
  faceTarget(targetWorld: THREE.Vector3): boolean {
    this._lungeDir.copy(targetWorld).sub(this.pos);
    this._lungeDir.addScaledVector(this.up, -this._lungeDir.dot(this.up)); // tangent plane
    if (this._lungeDir.lengthSq() < 1e-6) return false;
    this._lungeDir.normalize();
    this._faceHeading.copy(this.heading);
    // Auto-face, the GoW split: the BODY snaps fully onto the target — the arc sweeps at
    // `quat`, which derives from `facing`, so a part-turned body is a part-missed swing —
    // while the camera-owning `heading` only NUDGES toward it (clamped; a full camera snap
    // reads as hijack).
    this.facing.copy(this._lungeDir);
    const cos = Math.max(-1, Math.min(1, this.heading.dot(this._lungeDir)));
    const angle = Math.acos(cos);
    if (angle > 1e-3) {
      this.tmp.crossVectors(this.heading, this._lungeDir);
      const sign = this.tmp.dot(this.up) >= 0 ? 1 : -1;
      this.heading.applyAxisAngle(this.up, sign * Math.min(angle, FACE_MAX));
    }
    return true;
  }
  private _lungeDir = new THREE.Vector3();
  /** How much the held stick points at `targetWorld` (−1..1; 0 with no stick). */
  stickToward(targetWorld: THREE.Vector3): number {
    if (this.wish.lengthSq() < .09) return 0;
    this.tmp.copy(targetWorld).sub(this.pos);
    this.tmp.addScaledVector(this.up, -this.tmp.dot(this.up));
    if (this.tmp.lengthSq() < 1e-6) return 0;
    return this.tmp.normalize().dot(this.tmpB.copy(this.wish).normalize());
  }
  /** STRIKE CONTROL (the blade's "armed focus" — MH Wilds / Souls): while a
   *  swing plays, `strikeDrift` (m/s) is how far held movement may still carry
   *  the body, and `facingHold` keeps the swing's line (the hit window is not
   *  steerable). Refreshed every tick by the action system; lapses on its own
   *  if nothing refreshes it (another mode took over). */
  setStrikeControl(drift: number, holdFacing: boolean): void {
    this.strikeDrift = drift; this.facingHold = holdFacing; this.strikeControlT = .1;
  }
  private strikeDrift = 0;
  private facingHold = false;
  private strikeControlT = 0;
  /** End any strike step at once (a movement cancel hands the body back). */
  endStrikeStep(): void { if (this.strikeStepActive) { this.strikeStepActive = false; this.dashT = 0; } }

  /** STINGER — the soft-lock gap closer (locked target + LMB at range): while active, the
   *  tangential velocity IS the homing drive at STINGER_SPEED. The caller (main/lab) owns
   *  the dash: it re-aims via stingerSteer() every tick as the target moves and calls
   *  stingerEnd() on arrival/abort — this stays a dumb constant-speed carrier, like the
   *  roll's uniform drive. Exempt from the in-place combat root (the sd_dash clip plays
   *  on top). No i-frames: closing distance isn't dodging. */
  stingerActive = false;
  private stingerDir = new THREE.Vector3();
  private stingerDist = 0;
  private stingerHeight = 0;
  stingerSteer(targetWorld: THREE.Vector3, flightS?: number): void {
    if(this.diveLandingLocked)return;
    this.stingerDir.copy(targetWorld).sub(this.pos);
    this.stingerHeight = this.stingerDir.dot(this.up);
    this.stingerDir.addScaledVector(this.up, -this.stingerHeight); // tangent plane
    this.stingerDist = this.stingerDir.length();
    if (this.stingerDist < 1e-3) return;
    this.stingerDir.divideScalar(this.stingerDist);
    if (!this.stingerActive) {
      // LEAP off the ground on the activation edge — sized from the predicted flight time
      // (caller passes stingerFlightS) so the arc TOUCHES DOWN just before the target
      // (~90% of the flight airborne): the leap "lands" next to the enemy, and the slash
      // fires from the ground, never from mid-air. Capped so long closes stay subtle
      // (they touch down mid-flight and finish as a grounded slide).
      const leapV = flightS !== undefined ? Math.min(STINGER_LEAP_V, GRAVITY * flightS * 0.45) : STINGER_LEAP_V;
      const vUp = this.vel.dot(this.up);
      if (vUp < leapV) this.vel.addScaledVector(this.up, leapV - vUp);
      this.onGround = false;
      this.coyote = 0;
    }
    this.stingerActive = true;
    this.facing.copy(this.stingerDir); // the body commits onto the line (camera heading only eases)
  }
  /** AIR-EVADE ORBIT (1H, user call 2026-07-23): the airborne dodge no longer plays a
   *  dodge clip — with a locked target it CIRCLES them horizontally (the aerial float
   *  shows sd_air_idle). The ActionSystem re-feeds the live target per tick
   *  (airOrbitSteer); the tangential drive lives in the velocity chain. */
  airOrbitT = 0;
  private airOrbitDir = 1;
  private readonly airOrbitCenter = new THREE.Vector3();
  airOrbitStart(targetWorld: THREE.Vector3, side: "l" | "r"): void {
    this.airOrbitT = AIR_ORBIT_S;
    // up×toTarget points LEFT of the to-target line (right = heading×up convention), so a
    // RIGHT dodge needs the NEGATIVE tangent (user report: the orbit ran backwards).
    this.airOrbitDir = side === "r" ? -1 : 1;
    this.airOrbitCenter.copy(targetWorld);
  }
  airOrbitSteer(targetWorld: THREE.Vector3): void {
    this.airOrbitCenter.copy(targetWorld);
  }

  /** AIR FOCUS (user call 2026-07-24, revised): while AIRBORNE with a live lock the body
   *  faces the TARGET ITSELF — directly, not via the camera heading. The heading-magnetism
   *  route (strafeLock) is rate-capped, so a fast air evade out-runs it and the body swings
   *  AWAY mid-circle; and the orbit's own facing lasted only AIR_ORBIT_S, so the evade ended
   *  by snapping back onto the lagging heading. Fed per tick by the ActionSystem while the
   *  lock lives; null on the ground / lockless / non-melee. */
  private readonly airFocusPos = new THREE.Vector3();
  /** Feed freshness (s), refreshed per tick and decayed in update: the focus EXPIRES on its
   *  own, so a decision layer that stops feeding (ship entry, the 2H swap) can't leave the
   *  body staring at a stale point. */
  private airFocusT = 0;
  private get airFocusOn(): boolean {
    return this.airFocusT > 0;
  }
  setAirFocus(targetWorld: THREE.Vector3 | null): void {
    if (!targetWorld) { this.airFocusT = 0; return; }
    this.airFocusPos.copy(targetWorld);
    this.airFocusT = AIR_FOCUS_HOLD;
  }

  stingerEnd(): void {
    if (this.stingerActive) {
      // PLANT: shed the leap's carry down to a step so the arrival strike doesn't fly past
      // the target (the eased approach already brought it near STINGER_SPEED_END).
      const vUp = this.vel.dot(this.up);
      this.vTan.copy(this.vel).addScaledVector(this.up, -vUp);
      const m = this.vTan.length();
      if (m > 4) this.vel.addScaledVector(this.vTan, (4 - m) / m);
    }
    this.stingerActive = false;
  }
  /** The AIR RAVE is live (avatar.airChainLive, fed by main/lab per tick): descending
   *  gravity eases to RAVE_FALL_G so the string hangs between hits. */
  raveFloat = false;

  /** HIGH TIME — the launcher carries the ATTACKER up. Originally matched Sword_UpperCut's
   *  authored 2.0 m root rise; now 1.5× HIGHER by user call (2026-07-21): callers pass
   *  9.8 m/s → v²/2·GRAVITY(16) = 3.0 m apex, and the enemy's launch (9.8 ÷ LAUNCH_G 14 →
   *  ≈3.4 m) still crests with you. Leaves the jump chain at 1: hover/double-jump remain. */
  highTimeRise(v: number): void {
    this.vel.addScaledVector(this.up, v - Math.max(0, this.vel.dot(this.up)));
    this.airHopN = 0; // a launcher rise opens a FRESH air string (the hop ladder restarts)
    this.onGround = false;
    this.jumpCount = 1;
    this.coyote = 0;
  }

  /** GROUND POUND — the air-string SLAM: kill any rise and drive straight down hard. The
   *  swept blade drags the juggled target with it (negative `launch`); both arrive together.
   *  Ordinary landing rules apply on touchdown (SAFE_LANDING absorbs the plunge). */
  slamDown(v: number): void {
    const vUp = this.vel.dot(this.up);
    this.vel.addScaledVector(this.up, -(v + Math.max(0, vUp)));
  }

  /** AERIAL-combo float (the DMC air rave): each air swing arrests the fall and adds a small
   *  rise, so the string HANGS while the juggled target hangs with it. Radial only — carried
   *  horizontal momentum keeps flying. A hard fall (past −8 m/s) is too late to catch: the
   *  float is a combo suspension, not a parachute. Hop raised 2.6 → 4.2 across two user
   *  passes (2026-07-21: air hits should BOP the pair back up — with the RAVE_FALL_G eased
   *  descent, each hit visibly re-lifts; matches the juggle re-lift 3.5 → 5 victim-side).
   *
   *  DECAYING (user call 2026-07-24): each hop inside ONE airborne stint keeps less of
   *  AERIAL_HOP (the shared juggleLift ladder the victim's re-lift uses), and once the ladder
   *  is spent the swings stop floating you at all — the string sinks to the ground instead of
   *  hanging forever. `airHopN` resets the moment the feet touch down. */
  private airHopN = 0;
  /** True while the air-string can still hold the attacker up (drives the eased fall gravity). */
  private get raveLiftLeft(): boolean {
    return juggleLift(AERIAL_HOP, this.airHopN) > 0;
  }
  aerialHop(): void {
    const hop = juggleLift(AERIAL_HOP, this.airHopN);
    this.airHopN++;
    if (hop <= 0) return; // ladder spent — this swing rides the fall down
    const vUp = this.vel.dot(this.up);
    if (vUp > -8 && vUp < hop) this.vel.addScaledVector(this.up, hop - vUp);
  }

  /** Gather briefly before releasing a free burst along the latest camera aim. */
  private thirdTapAir():void{
    if(this.weaponMobility===0)return;
    if(this.wallJumpFlightPending){
      this.wallRun.release();this.wallJumpFlightPending=false;
    }
    this.jumpCount=3;
    if(this.energy<=0)return;
    this.flightStalled=false;this.stallT=0;this.jet='glide';
    this.glideApproach.amount=0;this.aimDiving=false;
    this.launchBoostT=FLIGHT_LAUNCH_TOTAL;this.launchRecovery.reset();
  }
  private doubleJump():void{
    const rise=this.vel.dot(this.up);
    if(rise<DOUBLE_JUMP_POP)this.vel.addScaledVector(this.up,DOUBLE_JUMP_POP-rise);
    this.jumpCount=2;this.jet='none';
    this.doubleJumpPending=true;this.doubleJumpAt=this.simT;
  }
  private readonly glidePhysics=new GlidePhysics();
  private readonly wallRun=new WallRunMotion();
  private readonly ledgeClimb=new LedgeClimbMotion();
  private ledgeDiveHeld=false;
  get ledgeClimbState():number{return this.ledgeClimb.state;}
  get ledgeClimbTime():number{return this.ledgeClimb.age;}
  /** Lip in the character's local render frame, also used by remote hand contacts. */
  readonly ledgeAnchor=new THREE.Vector3();
  private wallJumpFlightPending=false;
  private wallEnabled=true;
  get wallRunEnabled():boolean{return this.wallEnabled;}
  set wallRunEnabled(enabled:boolean){if(!enabled&&this.wallEnabled)this.wallRun.release();this.wallEnabled=enabled;}
  wallRunClearance=.88;
  get wallRunState():number{return this.jet==='none'?this.wallRun.state:0;}
  get wallRunTime():number{return this.wallRun.age;}
  private aimHover=false;
  /** ADS release stays supported until an explicit Space/Shift/X input. */
  private hoverHold=false;
  /** Flight ended mid-air (empty tank, released aim, X too low): a
   *  freefall that Space or Shift can turn straight back into flight. */
  private flightDropped=false;
  private aimFlightHeld=false;
  private aimFlightShiftHeld=false;
  private aimFlightSuppressed=false;
  private aimFlightCarry=0;
  private aimFlightRelease=0;
  /** A Shift break-out stays in flight until RMB is released and pressed again. */
  get flightAimActive():boolean{return this.aimHover;}
  private stallT=0;
  private launchBoostT=0;
  private readonly launchRecovery=new FlightLaunchRecovery();
  private aimDiving=false;
  private divePrepT=0;
  private diveKeyHeld=false;
  private diveLookDelta=0;
  private diveStartPitch=0;
  /** Travel direction when X was pressed; the pitch-over swings it down. */
  private readonly diveStartDir=new THREE.Vector3();
  private readonly diveTurn=new THREE.Quaternion();
  private readonly diveTurnFull=new THREE.Quaternion();
  /** Metres to the floor under a dive, including platforms far below. */
  diveFloorClearance=Infinity;
  private diveGuidedPitch=0;
  private diveReturnJet:'none'|'hover'|'glide'='glide';
  get divePreparation():number{return this.divePrepT>0?Math.max(.001,1-this.divePrepT/DIVE_PREPARATION.time):0;}
  get divePreparationRemaining():number{return this.divePrepT;}
  private cancelDivePreparation():void {
    if(this.divePrepT>0)this.jet=this.diveReturnJet;
    this.divePrepT=0;this.aimDiving=false;
  }
  get flightLaunchRemaining():number{return this.launchBoostT;}
  private readonly glideApproach=new GlideApproach();
  get flightApproach():number{return this.glideApproach.amount;}
  private flightSpaceTap=false;
  private flightSpaceT=0;
  /** Actual engine effort, for crystals/audio/network; coasting is zero. */
  flightPower=0;
  flightStalled=false;
  get flightSpeed():number{return this.vel.length();}
  private resumeHoverFlight():void{
    this.aimHover=false;this.hoverHold=false;this.flightDropped=false;this.jet='glide';
    this.aimFlightCarry=Math.max(GLIDE.recoverSpeed,this.aimFlightCarry);
    this.aimFlightRelease=.22;this.stallT=0;this.flightStalled=false;
    this.flightSpaceTap=false;
  }
  private dropEmptyFlight():void{
    if(!this.onGround&&this.energy<=0&&this.jet!=='none')this.dropFlight();
  }
  /** End flight into a freefall; Space/Shift resume it while fuel remains. */
  private dropFlight():void{
    this.jet='none';this.resetFlight();this.jumpCount=Math.max(3,this.jumpCount);
    this.flightDropped=true;
  }
  private resetFlight():void{
    this.launchRecovery.reset();
    this.aimHover=false;this.hoverHold=false;this.flightDropped=false;this.aimFlightSuppressed=false;this.aimFlightCarry=0;this.aimFlightRelease=0;
    this.wallJumpFlightPending=false;
    this.stallT=0;this.flightPower=0;this.flightStalled=false;this.launchBoostT=0;this.aimDiving=false;
    this.divePrepT=0;
    this.flightSpaceTap=false;this.flightSpaceT=0;this.glideApproach.amount=0;
  }
  /** Tangential speed, for the HUD and the gait. */
  groundSpeed = 0;
  /** Feet height above the surface (system units), refreshed each tick in clampToGround. ~0 when
   *  grounded; the avatar reads it to ANTICIPATE the big-leap landing (start the sprint-land clip a
   *  beat before touchdown so bl_loop crossfades into it). */
  groundClearance = 0;
  /**
   * Radial impact speed (m/s) of the landing that happened this tick, else
   * 0 — the fall-damage pipeline reads and thresholds it.
   */
  impactSpeed = 0;
  /** Wing state: hover, momentum glide, or X/steep-look dive. Releasing Shift keeps coasting. */
  jet: "none" | "hover" | "glide" | "dive" = "none";

  // --- ENERGY: the one pool (shared/src/combat.ts). Flight burns it, the arm cannon
  // and blade spend it, the shield absorbs damage with it. Upgrades raise the ceiling
  // and cut the burn. Runtime state — like fuel was, it is never persisted. ---
  energy = ENERGY_BASE;
  energyMax = ENERGY_BASE;
  /** Flight-upgrade multiplier on every jet burn rate below (1 = stock). */
  private burnMul = 1;
  private regenGround = 30;
  /** Regen multiplier the game sets from the active mode — shield mode recharges fastest.
   *  Zero while a guard is raised: bracing is not recharging. */
  regenMul = 1;
  /**
   * Seconds until the pool starts refilling again. Every COMBAT spend resets it — without
   * this the ground regen (fast, because flight is tuned around it) simply out-earns the
   * cannon's burn and you could hold the trigger down forever. Flight burns deliberately do
   * NOT set it: flight has its own rule (no recharge until grounded).
   */
  private combatBlockT = 0;

  /** Burn multiplier from the upgrade tracks alone — armor composes on top. */
  private upgradeBurnMul = 1;

  /** Adopt the derived stats for the player's current upgrade levels. */
  applyCombatStats(s: CombatStats): void {
    this.energyMax = s.energyMax;
    this.energy = Math.min(this.energy, s.energyMax);
    this.upgradeBurnMul = s.burnMul;
    this.burnMul = s.burnMul * this.armorBurnMul;
    this.regenGround = s.regenGround;
  }

  /** Armor mass/flow factor (v49). */
  private armorBurnMul = 1;

  /** "Shields first" (v50): while the shield recharges, the ONE stream belongs
   *  to it — action-energy regen runs at the gate fraction. Set per tick. */
  regenGate = 1;

  /** Emergency shield→energy draw (v50): installed by main; returns energy
   *  actually granted for `need`. Only ACTION spends draw — passive movement
   *  burn never silently eats the shield. */
  shieldDraw: ((need: number) => number) | null = null;

  /** Adopt the worn kit's energy economy (v49 armor: MASS raises burn, FLOW
   *  raises regen — armor shapes the envelope, never the damage). */
  applyArmor(burnMul: number, regenMul: number): void {
    this.armorBurnMul = burnMul;
    this.burnMul = this.upgradeBurnMul * burnMul;
    this.regenMul = regenMul;
  }

  /** Pay for a COMBAT action. False (and nothing spent) if the pool can't cover it.
   *  When the pool is short, the shield covers the gap (lossy — see SHIELD_DRAW_RATIO):
   *  the emergency reserve, spent deliberately by acting, never by moving. */
  spendEnergy(cost: number): boolean {
    if (this.energy < cost && this.shieldDraw) this.energy += this.shieldDraw(cost - this.energy);
    if (this.energy < cost) return false;
    this.energy -= cost;
    this.combatBlockT = COMBAT_REGEN_DELAY;
    return true;
  }

  /** Bleed energy for a COMBAT effect that isn't all-or-nothing (shield absorption, guard upkeep). */
  drainEnergy(amount: number): void {
    if (amount <= 0) return;
    this.energy = Math.max(0, this.energy - amount);
    this.combatBlockT = COMBAT_REGEN_DELAY;
  }

  /** Flight burn: spends from the same pool, but does not stall the recharge (see combatBlockT). */
  private burnEnergy(amount: number): void {
    this.energy = Math.max(0, this.energy - amount);
  }

  /** True when the pool may refill this tick (no recent combat spend). */
  private canRegen(): boolean {
    return this.combatBlockT <= 0;
  }
  /** Boost-run: the top sprint gear (hold Shift + move forward; ramps in after BOOST_RAMP). */
  boosting = false;
  /** Eased boost weight (0..1) — drives the camera pull-back + the slowed boost stride. */
  boostK = 0;
  private boostT = 0; // sustained forward-sprint timer (s) toward the boost ramp
  /** Super-jump charge, 0..1 — set while holding Space past the hold threshold; drives the charge arc + pose. */
  jumpChargeFrac = 0;
  /** True while CHARGING a VERTICAL super-jump (holding Space past JUMP_HOLD, on the ground, NOT a boost
   *  big-leap) — the avatar holds the SuperHero takeoff windup, firing the launch on release. */
  superJumpCharging(): boolean {
    return SUPER_JUMP_ENABLED && this.chargingJump && !this.chargeBoost && this.spaceHeldT >= JUMP_HOLD;
  }
  private coyote = 0;
  private jumpCount = 0;
  private waterLeapGrace = 0; // >0 ⇒ just leapt out of water; forced airborne so buoyancy can't recapture
  private doubleJumpAt = -Infinity;
  private groundedFor = 0;
  private simT = 0;
  private spaceHeldT = 0; // Space held (s) on the ground — winds up the super-jump charge
  private chargingJump = false; // a grounded press is being held for a (possibly charged) jump
  private jetGrace = 0; // the jet stays engaged this long without Space (super-jump takeoff bridge)
  /** Set on a boost BIG-LEAP launch (consumed by main.ts → avatar.flagBigLeap for the sprint-start→loop→
   *  sprint-land anim). One-shot, mirrors superJumpPending. */
  bigLeapPending = false;
  /** True while airborne in a boost big-leap: the horizontal bound COASTS (air-steerable, no momentum
   *  bleed) and the leap keeps burning boost fuel. Cleared at touchdown. */
  bigLeapActive = false;
  /** Set on the frame a DIVE (X-plunge) touches down: main.ts → avatar.flagDiveLand() plays the
   *  SuperHero flight-land flourish. One-shot, mirrors bigLeapPending. */
  diveLandPending = false;
  /** Dive-landing PAUSE countdown: movement + jump rooted while > 0 (the slam beat — the flight-land
   *  flourish plays out instead of being instantly run out of). */
  private diveLandPauseT = 0;
  get diveLandingLocked():boolean{return this.diveLandPauseT>0&&this.onGround;}
  /** "Fly on the next air-tap": set by ANY super-jump / big-leap launch. While airborne with this set,
   *  a Space tap converts straight to flight (glide) — no tight double-jump window. Cleared at touchdown. */
  private superLeap = false;
  /** Latched at the charge-press: was the player boost-running? A boost jump becomes a forward big-leap
   *  on release; a normal grounded charge becomes the vertical super-jump. */
  private chargeBoost = false;
  private facing = new THREE.Vector3(0, 0, -1);
  private readonly poseRight = new THREE.Vector3();
  private center = new THREE.Vector3();
  private dir = new THREE.Vector3();
  private right = new THREE.Vector3();

  /** Directional animation input plus measured movement. Signed speeds use
   *  the body's tangent frame: +forward, +right, +climb, all in m/s. */
  moveDirLocal(): { fwd: number; right: number; climb: number; lateralSpeed?: number; forwardSpeed?: number } {
    this.poseRight.crossVectors(this.facing, this.up).normalize();
    return { fwd: this.wish.dot(this.facing), right: this.wish.dot(this.right), climb: this.vel.dot(this.up),
      lateralSpeed: this.vel.dot(this.poseRight), forwardSpeed: this.vel.dot(this.facing) };
  }

  /** Jump-chain count: 0 grounded/walked-off, 1 after the first jump (the window where a
   *  double-jump to hover is still pending), 2 after the double-jump, 3 after a glide/strafe.
   *  The avatar uses ===1 to hold the leap pose (not a fall) through the pre-hover descent. */
  get jumpChain(): number {
    return this.jumpCount;
  }
  /** Boost-run wind-up progress, 0..1 — drives the countdown arc. Stays 0 for the first BOOST_ARC_DELAY
   *  seconds of sprint (the arc only appears after that), then fills to 1 as boost engages. */
  get boostRampFrac(): number {
    return Math.max(0, Math.min(1, (this.boostT - BOOST_ARC_DELAY) / (BOOST_RAMP - BOOST_ARC_DELAY)));
  }
  private wish = new THREE.Vector3();
  private vUp = new THREE.Vector3();
  private vTan = new THREE.Vector3();
  private tmp = new THREE.Vector3();
  private tmpB = new THREE.Vector3();
  private zero = new THREE.Vector3();
  private tmpM = new THREE.Matrix4();
  private tmpQ = new THREE.Quaternion();
  private normalArr: number[] = [0, 1, 0];
  private _wc: WaterColumn = { floorR: 0, floorDepth: 0, surfR: -1e9, surfDepth: -1e9, riverK: 0, flowSpeed: 0, fx: 0, fy: 0, fz: 0 };

  /** The LOCAL water-surface radius under the feet (river ws inland, sea
   *  elsewhere) from the last update tick — where splash/wake FX belong.
   *  Only meaningful while waterState ≠ "land". */
  get waterSurfR(): number {
    return this._wc.surfR;
  }
  private _localDir = new THREE.Vector3();
  private _current = new THREE.Vector3();
  private _exitCurrent = new THREE.Vector3();
  /** One frame's total displacement, walked in solid-safe slices (SOLID_STEP_M). */
  private _stepD = new THREE.Vector3();
  private pendingTerrainSeat = false;

  /** Snap the interpolation history (spawns/teleports — no smearing across). */
  snapVisual(): void {
    this.prevPos.copy(this.pos);
    this.prevQuat.copy(this.quat);
  }

  /**
   * Step out of the ship MID-AIR: the character drops from beneath the
   * hull carrying a little of its momentum. Space double-jumps, then deploys flight;
   * held Shift powers flight. Preserve the remaining energy reserve.
   */
  spawnAirborne(
    shipPos: THREE.Vector3,
    shipQuat: THREE.Quaternion,
    shipVel: THREE.Vector3,
    planet: PlanetHost,
  ): void {
    this.pendingTerrainSeat = false;
    this.center.copy(planet.pose.center);
    this.up.copy(shipPos).sub(this.center).normalize();
    this.pos.copy(shipPos).addScaledVector(this.up, -3); // out the belly hatch
    // Face along the ship's nose, projected to the horizon.
    this.heading.set(0, 0, -1).applyQuaternion(shipQuat);
    this.heading.addScaledVector(this.up, -this.heading.dot(this.up));
    if (this.heading.lengthSq() < 1e-6) this.heading.set(1, 0, 0).cross(this.up);
    this.heading.normalize();
    this.facing.copy(this.heading);
    this.pitch = 0.5; // start looking down at the drop
    // Carry some of the ship's momentum, capped — a 150 m/s disembark is a
    // dramatic exit, not an unrecoverable horizontal cannonball.
    this.vel.copy(shipVel).multiplyScalar(0.5);
    if (this.vel.length() > 30) this.vel.setLength(30);
    this.onGround = false;
    this.sliding = false;
    this.groundSpeed = 0;
    this.jet = "none";
    this.diveLandPauseT=0;this.diveLandPending=false;this.resetFlight();this.wallRun.reset();this.ledgeClimb.reset();
    // The chain starts spent-one: first Space tap = double jump, re-tap = glide.
    this.jumpCount = 1;
    this.updateQuat();
    this.snapVisual();
  }

  /**
   * Login resume: stand back up exactly where the player logged out.
   *
   * `keepAltitude` is for handoffs that place the body at a REAL position it is
   * already at — stepping off a vehicle — rather than teleporting it in. The
   * default groundHard drops the feet onto the surface from any height, which
   * for a dismount reads as being warped to the ground the moment you let go of
   * a hovering (or airborne, or roof-parked) chassis. With it set, the ordinary
   * snap/fall rules apply instead: glue within SNAP_DIST, otherwise fall.
   */
  restoreAt(
    p: [number, number, number],
    q: [number, number, number, number],
    planet: PlanetHost,
    opts?: { keepAltitude?: boolean; preserveEnergy?: boolean },
  ): void {
    this.pos.set(...p);
    this.center.copy(planet.pose.center);
    this.up.copy(this.pos).sub(this.center).normalize();
    // Recover the look direction from the saved orientation (-Z forward),
    // projected back onto the tangent plane.
    this.facing.set(0, 0, -1).applyQuaternion(this.tmpQ.set(...q).normalize());
    this.facing.addScaledVector(this.up, -this.facing.dot(this.up));
    if (this.facing.lengthSq() < 1e-6) this.facing.set(1, 0, 0).cross(this.up);
    this.facing.normalize();
    this.heading.copy(this.facing);
    this.pitch = 0.3;
    this.vel.set(0, 0, 0);
    this._current.set(0, 0, 0);
    this.waterState = "land";
    this.waterLeapGrace = 0;
    this.groundSpeed = 0;
    if(!opts?.preserveEnergy)this.energy = this.energyMax;
    this.jet = "none";
    this.diveLandPauseT=0;this.diveLandPending=false;this.resetFlight();this.wallRun.reset();this.ledgeClimb.reset();
    this.jumpCount = 0;
    this.pendingTerrainSeat = planet.terrainSolidsReady === false && !opts?.keepAltitude;
    if (planet.terrainSolidsReady === false) this.onGround = false;
    else if (opts?.keepAltitude) this.clampToGround(planet, true);
    else this.groundHard(planet);
    this.updateQuat();
    this.snapVisual();
  }

  /** Re-seat an already restored character after asynchronous spawn terrain
   * has finished adopting its settlement carve/colliders. Preserves facing. */
  seatOnGround(planet: PlanetHost): void {
    this.wallRun.reset();this.ledgeClimb.reset();
    this.center.copy(planet.pose.center);
    this.vel.set(0, 0, 0);
    this.groundHard(planet);
    this.updateQuat();
    this.snapVisual();
  }

  /**
   * Place the feet ON the surface along the current radial, however far
   * away it is — spawns and restores must never leave the character
   * floating at ship altitude (clampToGround only glues within snap range).
   */
  private groundHard(planet: PlanetHost): void {
    if (planet.terrainSolidsReady === false) { this.pendingTerrainSeat = true; this.onGround = false; return; }
    this.dir.copy(this.pos).sub(this.center).normalize();
    let ground = planet.surfaceRadiusWorld(this.dir);
    if (planet.def.seaRadius !== null || planet.sampler.inlandWetlands?.().length) {
      // Over deep water, place FLOATING at the swim line rather than the deep seabed.
      this._localDir.copy(this.dir).applyQuaternion(planet.pose.invQuat);
      waterColumn(planet.sampler, planet.def, this._localDir.x, this._localDir.y, this._localDir.z, this._wc);
      if (this._wc.surfDepth > WADE_MAX) ground = this._wc.surfR - SWIM_SUBMERGE;
    }
    // Dropped over a building: seat on its roof, not inside its walls.
    const stand = this.roofStand(planet);
    if (stand !== null && stand > ground) ground = stand;
    const cliff = this.terrainContacts.support(planet, this.pos, .01, Math.max(0, this.pos.distanceTo(this.center) - ground) + .1);
    if (cliff !== null && cliff > ground) ground = cliff;
    this.pos.copy(this.center).addScaledVector(this.dir, ground);
    this.clampToGround(planet, true); // normal/onGround bookkeeping
  }

  /** Camera look can remain live while movement is locked, as in a race countdown. */
  updateLook(input: Pick<Input, 'consumeMouse' | 'invertY'>): void {
    // --- Mouse: yaw spins the heading around up, dy drives camera pitch ---
    // lookScale rides the live ADS zoom (main feeds camera.fov / base each frame):
    // under a 3× scope the same wrist motion must cover 1/3 the world angle, or
    // the zoom is unusable. 1 whenever the camera is unzoomed.
    const { dx, dy } = input.consumeMouse();
    const sens = MOUSE_SENS * this.lookScale;
    this.diveLookDelta=dy*sens*(input.invertY?-1:1);
    this.heading.applyAxisAngle(this.up, -dx * sens);
    // Near-vertical both ways: shooting drones straight overhead is legal.
    this.pitch = Math.max(
      -1.45,
      Math.min(1.45, this.pitch + dy * sens * (input.invertY ? -1 : 1)),
    );
    this.right.crossVectors(this.heading, this.up);
  }

  // prevPos/prevQuat are snapshotted by the CALLER before the body carry —
  // the render lerp must cover the carry too (see ship.update note).
  update(dt: number, input: Input, planet: PlanetHost): void {
    // The combat recharge lockout runs on the wall clock, not on whether a regen branch
    // happened to be reached — otherwise it would never expire while airborne.
    if (this.combatBlockT > 0) this.combatBlockT = Math.max(0, this.combatBlockT - dt);
    // --- Local frame: radial up, parallel-transported heading ---
    this.center.copy(planet.pose.center);
    this.up.copy(this.pos).sub(this.center).normalize();
    this.heading.addScaledVector(this.up, -this.heading.dot(this.up)).normalize();
    this.updateLook(input);
    const diveHeld=input.isDown('KeyX');
    const divePressed=diveHeld&&!this.diveKeyHeld;
    this.diveKeyHeld=diveHeld;
    // Geometry and its physics arrive atomically. Retain the requested restore
    // altitude while waiting instead of seating on the obsolete radial floor.
    if (planet.terrainSolidsReady === false) { this.onGround = false; this.groundSpeed = 0; this.updateQuat(); return; }
    if (this.pendingTerrainSeat) { this.pendingTerrainSeat = false; this.groundHard(planet); this.snapVisual(); }
    // Aim and pace are independent: holding Shift changes speed without lowering the item.
    const rmbDown = input.isButtonDown(2);
    // TAP-vs-HOLD discrimination (the sword parry, 2026-07-22): with aimDelayS set (melee
    // mode — PARRY_TAP_S), the aim/focus engages only once RMB has been HELD past the
    // window; a quick tap never flashes the focus stance (the tap is the parry, handled
    // by the action system on the release edge). Gun modes leave the delay 0 — unchanged.
    this.rmbHeldT = rmbDown ? this.rmbHeldT + dt : 0;
    const heldPastTap = this.aimDelayS <= 0 || this.rmbHeldT >= this.aimDelayS;
    const aimActive = (rmbDown && heldPastTap) || this.forceAim;
    const shift=input.isDown('ShiftLeft')||input.isDown('ShiftRight');
    const aimPressed=aimActive&&!this.aimFlightHeld,shiftPressed=shift&&!this.aimFlightShiftHeld;
    this.aimFlightHeld=aimActive;this.aimFlightShiftHeld=shift;
    this.dropEmptyFlight();
    if(!aimActive)this.aimFlightSuppressed=false;
    if(this.onGround||this.actionAirLock||this.jet==='none'){
      this.aimHover=false;this.aimFlightCarry=0;this.aimFlightRelease=0;
    }else if(aimPressed&&!this.aimFlightSuppressed){
      this.aimFlightCarry=Math.min(22,this.vel.length());
      this.cancelDivePreparation();this.launchBoostT=0;this.launchRecovery.reset();
      this.jet='hover';this.aimHover=true;this.hoverHold=true;this.aimFlightRelease=0;
      this.flightStalled=false;this.stallT=0;this.glideApproach.amount=0;
    }
    if(this.aimHover){
      if(!aimActive){
        // Letting go of the aim ends the hover: the pilot falls, and Space or
        // Shift picks flight straight back up.
        this.dropFlight();
      }else if(shiftPressed&&!aimPressed&&this.energy>0){
        this.resumeHoverFlight();this.aimFlightSuppressed=true;
      }else this.aimFlightCarry*=Math.exp(-.28*dt);
    }
    // A dropped flight resumes on a fresh press only: a Shift held from before
    // the aim or the X does not relaunch the moment the pilot lets go.
    if(!this.onGround&&(this.jet==='hover'&&(!this.hoverHold||shiftPressed)||this.flightDropped&&shiftPressed)&&!this.aimHover&&shift
      &&!this.actionAirLock&&this.energy>0)this.resumeHoverFlight();
    this.aiming=aimActive&&!this.aimFlightSuppressed&&this.jet!=='glide'&&this.jet!=='dive';

    // --- Wish direction (camera-relative, tangent plane) ---
    this.wish.set(0, 0, 0);
    if (input.isDown("KeyW")) this.wish.add(this.heading);
    if (input.isDown("KeyS")) this.wish.sub(this.heading);
    if (input.isDown("KeyD")) this.wish.add(this.right);
    if (input.isDown("KeyA")) this.wish.sub(this.right);
    const moving = this.wish.lengthSq() > 0;
    if (moving) this.wish.normalize();
    // Crouch: X toggles while grounded; auto-stand when airborne. Slower, no sprint.
    // (Airborne, the same key is the DIVE hold — the onGround gate keeps them apart.)
    const wasCrouching = this.crouching;
    if ((input.wasPressed("KeyX")) && this.onGround && !this.diveLandingLocked) {
      this.crouching = !this.crouching;
    }
    this.crouching = this.crouching && this.onGround;
    if (wasCrouching && !this.crouching && this.terrainContacts.occupied(planet, this.pos, 1.8)) this.crouching = true;
    this.iframeT = Math.max(0, this.iframeT - dt);
    this.dashT = Math.max(0, this.dashT - dt);
    this.strikeControlT = Math.max(0, this.strikeControlT - dt);
    this.airOrbitT = Math.max(0, this.airOrbitT - dt);
    if (this.onGround) this.airOrbitT = 0; // touchdown ends the air circle
    this.airFocusT = Math.max(0, this.airFocusT - dt); // the lock's facing feed goes stale on its own
    if (this.dashT <= 0) { this.dashSpeed = 0; this.strikeStepActive = false; } // drives end with their window
    this.dashCooldownT = Math.max(0, this.dashCooldownT - dt);
    this.diveLandPauseT = Math.max(0, this.diveLandPauseT - dt);

    // --- Water column under the feet → land / wade / swim + river current ---
    const seaR = planet.def.seaRadius;
    const rNow = this.pos.distanceTo(this.center);
    const wasSwimming = this.waterState === "swim";
    // Current is integrated as displacement while swimming. Transfer it into
    // velocity on exit so a waterfall carries the swimmer past the lip.
    this._exitCurrent.copy(this._current);
    let curMag = 0;
    this._current.set(0, 0, 0);
    if (seaR !== null || planet.sampler.inlandWetlands?.().length) {
      this._localDir.copy(this.up).applyQuaternion(planet.pose.invQuat);
      waterColumn(planet.sampler, planet.def, this._localDir.x, this._localDir.y, this._localDir.z, this._wc);
      // River-aware: surfR is the LOCAL water surface (sea, or a river's ws when
      // that stands higher) — inland rivers are real, swimmable water now.
      const feetSub = this._wc.surfR - rNow; // >0 ⇒ feet below the local water surface
      const deep = this._wc.surfDepth > WADE_MAX;
      if (this.waterLeapGrace > 0) {
        // Just leapt out of the water: stay airborne (normal gravity + the air jump
        // chain) for a moment so the buoyancy spring can't yank the body back under.
        this.waterLeapGrace -= dt;
        this.waterState = "land";
      } else if (deep && feetSub > -0.25) this.waterState = "swim"; // over deep water, at/under the surface
      else if (!deep && feetSub > 0.15 && this._wc.surfDepth > 0.15) this.waterState = "wade";
      else this.waterState = "land";
      // River current: per-point speed from the graph (steep reaches run fast),
      // slack toward the banks so the edge is always an escape route.
      if (this.waterState !== "land" && this._wc.flowSpeed > 0 && this._wc.riverK > 0.05) {
        this._current.set(this._wc.fx, this._wc.fy, this._wc.fz).applyQuaternion(planet.pose.quat);
        if (this._current.lengthSq() > 1e-6) {
          const slack = CURRENT_BANK_SLACK + (1 - CURRENT_BANK_SLACK) * Math.min(1, this._wc.riverK);
          curMag = this._wc.flowSpeed * slack;
          this._current.normalize().multiplyScalar(curMag);
        }
      }
    } else {
      this.waterState = "land";
    }
    if (wasSwimming && this.waterState === "land") this.vel.add(this._exitCurrent);
    const subTarget = this.waterState === "swim" ? 1 : this.waterState === "wade" ? Math.min(1, (this._wc.surfR - rNow) / 1.6) : 0;
    this.submergeK += (subTarget - this.submergeK) * (1 - Math.exp(-8 * dt));
    if (this.waterState === "swim") { this.ledgeClimb.cancel();this.swimUpdate(dt, input, planet, rNow, curMag); return; }

    const jumpPressed=input.wasPressed('Space')&&!this.actionAirLock&&!this.diveLandingLocked;

    const ledgeAllowed=this.wallRunEnabled&&this.waterState==='land'&&!this.crouching&&!this.aiming
      &&!this.actionAirLock&&!this.evadeReady&&!this.stingerActive&&this.dashT<=0&&!this.diveLandingLocked;
    const flyingToLedge=this.jet!=='none';
    const ledgeDive=input.isDown('KeyX');
    if(!ledgeDive||!this.ledgeClimb.state)this.ledgeDiveHeld=false;
    // A coasting pilot need not hold W. Look/travel toward the lip is enough;
    // ordinary jumps still require directional input. X is dive intent in
    // flight; after a catch, release/re-press X to let go of the lip.
    this.solidPrevious.copy(this.pos);
    let ledge=this.ledgeClimb.step(dt,planet,this.pos,this.vel,this.up,moving?this.wish:this.heading,
      ledgeAllowed&&(moving||flyingToLedge||this.ledgeClimb.state!==0),this.onGround,
      input.isDown('KeyS')||(!flyingToLedge&&!this.ledgeDiveHeld&&ledgeDive),flyingToLedge);
    if (ledge && this.terrainContacts.move(planet, this.solidPrevious, this.pos, this.vel, 1.8, WALL_RADIUS)) {
      this.ledgeClimb.cancel(); ledge = null;
    }
    if(ledge){
      this.simT+=dt;
      if(flyingToLedge)this.ledgeDiveHeld=ledgeDive;
      this.applyLedgeMotion(ledge,dt,planet);return;
    }

    const wallAllowed=this.wallRunEnabled&&this.waterState==='land'&&!this.crouching&&!this.aiming&&!this.actionAirLock
      &&!this.evadeReady&&!this.stingerActive&&this.dashT<=0&&!this.diveLandingLocked
      &&this.jet!=='dive'&&!input.isDown('KeyX')&&!input.isDown('KeyS');
    if(Math.abs(this.wallRun.state)===3&&(this.onGround||this.jet!=='none'))this.wallRun.release();
    // Settle before rearming: touchdown momentum must not pull the player
    // straight back up the same wall after an exhausted run.
    if(this.onGround)this.wallRun.landed(this.groundSpeed<WALL_RUN.minSpeed);
    // Departure follows camera aim independently of held strafe/movement input.
    const wallLook=this.tmp.copy(this.heading).multiplyScalar(Math.cos(this.pitch)).addScaledVector(this.up,-Math.sin(this.pitch));
    this.solidPrevious.copy(this.pos);
    const wallMotion=this.wallRun.step(dt,planet,this.pos,this.vel,this.up,wallAllowed,jumpPressed,GRAVITY*sizeG(planet.def.radius),this.wallRunClearance,moving?this.wish:undefined,moving,shift,wallLook);
    if (wallMotion) this.terrainContacts.move(planet, this.solidPrevious, this.pos, this.vel, 1.8, WALL_RADIUS);
    if(wallMotion){
      // The wall owns this tick, including Space: a jump-off leaves exactly
      // two jumps spent. Only a subsequent press may enter flight.
      this.simT+=dt;this.jet='none';this.resetFlight();this.onGround=false;
      this.wallJumpFlightPending=wallMotion==='jump';
      this.jumpCount=2;this.coyote=0;this.groundedFor=0;this.doubleJumpPending=false;
      this.superLeap=false;this.bigLeapActive=false;this.chargingJump=false;
      this.sprinting=false;this.boosting=false;this.boostT=0;this.walkMode=false;
      this.advanceSolids(dt,planet);
      this.clampToGround(planet,false);this.impactSpeed=0;
      this.groundSpeed=this.tmp.copy(this.vel).addScaledVector(this.up,-this.vel.dot(this.up)).length();
      // Catch the surface immediately, but let the visible body turn into the
      // stride over the same short beat as the pose handoff.
      this.facing.lerp(this.wallRun.tangent,1-Math.exp(-18*dt)).normalize();this.updateQuat();
      if(this.onGround){this.wallRun.release();this.wallJumpFlightPending=false;this.jumpCount=0;}
      return;
    }

    // A hard landing no longer staggers/slows the player — the anticipated landing ANIMATION (avatar) is
    // the whole consequence now (plus a small fall-hp sting in main.ts). No forced crouch.
    const walking = input.isDown("AltLeft") || input.isDown("AltRight");
    this.sprinting = moving && this.jet === 'none' && !this.crouching && !walking && shift && this.waterState !== "wade";
    this.walkMode = this.heldItemId === null && this.aiming && !this.sprinting;
    // BOOST-RUN: a sustained FORWARD sprint with fuel escalates into the top gear. moving forward =
    // the wish points along the heading; after BOOST_RAMP of continuous forward-sprint it engages and
    // sips fuel (burned below, in the grounded branch). Drops the moment you stop sprinting forward or
    // run dry. boostK eases the camera pull-back + the slowed boost stride.
    const fwdInput = moving ? this.wish.dot(this.heading) : 0;
    // PARKED: SUPER_SPRINT_ENABLED=false leaves the complete ramp/boost path
    // below dormant while held Shift selects ordinary sprint.
    const wantBoost = SUPER_SPRINT_ENABLED && this.sprinting && this.onGround && fwdInput > 0.4 && this.energy > BOOST_MIN_FUEL && this.waterState === "land";
    this.boostT = wantBoost ? this.boostT + dt : 0;
    this.boosting = this.boostT > BOOST_RAMP && this.energy > 0;
    this.boostK += ((this.boosting ? 1 : 0) - this.boostK) * (1 - Math.exp(-6 * dt));
    // Default ground pace is a RUN; held Shift sprints. (WALK_SPEED unused.)
    // In-place combat strike (combatSpeed→0) HARD-ROOTS the fighter so a held strafe can't slide it.
    // The evade-dash is live while dashT > 0 in the stance (the TRAVEL window, longer than the i-frames)
    // — it coasts the whole roll, exempt from the in-place root below, so the dodge actually moves.
    // The i-frame/cooldown terms keep a from-neutral dodge coasting through its FIRST ticks,
    // before main.ts has re-derived combatStance from the (just-started) dodge one-shot.
    const dashing = this.dashT > 0 && (this.combatStance || this.iframeT > 0 || this.dashCooldownT > 0);
    const combatRooted = isCombatRooted(this.combatStance, this.combatSpeed, dashing);
    const targetSpeed = (this.crouching ? CROUCH_SPEED : this.combatStance ? this.combatSpeed : walking || this.walkMode ? WALK_MODE_SPEED : this.boosting ? BOOST_SPEED : this.sprinting ? SPRINT_SPEED : RUN_SPEED) * (this.waterState === "wade" ? WADE_SLOW : 1);

    // --- Slope at the feet (analytic normal, sub-meter sample arc) ---
    this.dir.copy(this.pos).sub(this.center);
    const r = this.dir.length() || 1;
    this.dir.divideScalar(r);
    planet.surfaceNormalWorld(this.dir, this.normalArr, SLOPE_EPS);
    const cliffSlope = this.terrainContacts.support(planet, this.pos);
    if (cliffSlope !== null && Math.abs(r - cliffSlope) < .12) this.terrainContacts.normal.toArray(this.normalArr);
    const slopeCos =
      this.normalArr[0] * this.up.x + this.normalArr[1] * this.up.y + this.normalArr[2] * this.up.z;
    const walkable = slopeCos > WALKABLE_COS;

    // --- Velocity, split into radial + tangential ---
    this.vUp.copy(this.up).multiplyScalar(this.vel.dot(this.up));
    this.vTan.copy(this.vel).sub(this.vUp);

    // Steering is never fully cut: on steep ground the clamp strips the
    // into-slope component (you can't climb), but traversing stays possible.
    // Hovering gets strong thruster-assisted air control. Glide steering and
    // braking are applied by the momentum solve below.
    const accel =
      this.onGround && walkable
        ? GROUND_ACCEL
        : this.jet === "hover"
          ? HOVER_ACCEL
          : this.sliding
            ? AIR_ACCEL * 2.4
            : AIR_ACCEL;
    if (this.diveLandingLocked) {
      // The landing beat owns movement ahead of dash/lunge drives.
      this.vTan.set(0,0,0);
    } else if (this.airOrbitT > 0 && !this.onGround) {
      // AIR-EVADE ORBIT: tangential circle around the (live-fed) target — v = ω·r along
      // the side-signed tangent; the vertical damps toward a hover so the circle stays
      // horizontal. Ends by its own clock or at touchdown.
      this.tmp.copy(this.airOrbitCenter).sub(this.pos);
      this.tmp.addScaledVector(this.up, -this.tmp.dot(this.up));
      const orbitDist = this.tmp.length();
      if (orbitDist > 0.5) {
        this.tmp.divideScalar(orbitDist);
        this.tmpB.crossVectors(this.up, this.tmp).multiplyScalar(this.airOrbitDir);
        this.vTan.copy(this.tmpB).multiplyScalar(Math.min(AIR_ORBIT_MAX, AIR_ORBIT_RATE * orbitDist));
      }
      const vUpO = this.vel.dot(this.up);
      this.vel.addScaledVector(this.up, -vUpO * (1 - Math.exp(-12 * dt))); // hover the circle
    } else if (this.stingerActive) {
      // STINGER: the tangential velocity IS the homing drive — full speed in the open,
      // easing down across the last STINGER_SLOW_R metres (the leap "lands" onto the
      // target instead of flying by). The caller ends the dash the tick it arrives.
      const k = Math.min(1, Math.max(0, (this.stingerDist - 2.3) / STINGER_SLOW_R));
      this.vTan.copy(this.stingerDir).multiplyScalar(STINGER_SPEED_END + (STINGER_SPEED - STINGER_SPEED_END) * k);
    } else if (dashing) {
      // EVADE-DASH. UNIFORM mode (dashSpeed > 0 — the roll): HOLD the dodge speed for the
      // whole window, re-normalized along the current tangent, so the tumble is one constant
      // motion. Legacy mode (attackDash lunges): friction-only impulse coast. Both are exempt
      // from the in-place root and the combat-speed easing; the clip plays on top.
      if (this.strikeStepActive) {
        // STRIKE STEP: smootherstep distance (speed 30u²(1−u)²·D/T peaks at contact)
        // plus the decaying carry, along the step line re-projected onto the tangent.
        const u = Math.min(1, this.strikeStepT / this.strikeStepDur);
        const drive = this.strikeStepDist * 30 * u * u * (1 - u) * (1 - u) / this.strikeStepDur
          + this.strikeStepCarry * Math.exp(-STRIKE_CARRY_DECAY * this.strikeStepT);
        this.strikeStepDir.addScaledVector(this.up, -this.strikeStepDir.dot(this.up)).normalize();
        this.vTan.copy(this.strikeStepDir).multiplyScalar(drive);
        this.strikeStepT += dt;
        if (this.strikeStepT >= this.strikeStepDur) this.strikeStepActive = false;
      } else if (this.dashSpeed > 0) {
        // Elden-Ring profile: k = remaining fraction; fast start (k≈1 → START), easing down
        // to END by the window's close — and END is a WALKING pace, so nothing ever stops.
        const k = this.dashWindow > 1e-4 ? this.dashT / this.dashWindow : 0;
        const spd = DODGE_SPEED_END + (this.dashSpeed - DODGE_SPEED_END) * k * k;
        const m = this.vTan.length();
        if (m > 1e-4) this.vTan.multiplyScalar(spd / m);
      } else {
        this.vTan.multiplyScalar(Math.exp(-this.dashFriction * dt));
      }
    } else if (combatRooted) {
      // An in-place strike plants the fighter — except the blade, whose held
      // movement still carries it slowly through the swing (strikeDrift): the
      // swing never takes the stick away. Kicks and the rest still plant hard.
      const drift = this.strikeControlT > 0 ? this.strikeDrift : 0;
      if (drift > 0 && this.wish.lengthSq() > .04) {
        this.tmpB.copy(this.wish).multiplyScalar(drift);
        this.vTan.lerp(this.tmpB, 1 - Math.exp(-10 * dt));
      } else this.vTan.set(0, 0, 0);
    } else if (this.jet === "hover" && shift && !this.aimHover && !this.hoverHold && !this.actionAirLock && this.energy > 0) {
      // Boost builds from the carried velocity; hover must not brake against it.
    } else if (this.jet === "hover" || this.jet === "dive") {
      // HOVER/DIVE reclaim control FAST: ease the tangential velocity toward the wished velocity (0 with
      // no WASD) so momentum carried in — a super-jump apex, a fast fall — bleeds off in a fraction of a
      // second instead of coasting away. (Replaces the old constant-accel steer, which took ~2s to shed a
      // fast entry and never damped a no-input hover.) The DIVE variant is the drop-and-drive steer:
      // WASD drifts the plunge laterally (camera-relative), neutral eases it back to straight-down.
      const spd = this.jet === "dive" ? DIVE_DRIFT_SPEED : HOVER_SPEED;
      const rate = this.jet === "dive" ? DIVE_CONTROL : HOVER_CONTROL;
      this.tmp.copy(this.wish).multiplyScalar(moving ? spd : 0);
      this.vTan.lerp(this.tmp, 1 - Math.exp(-rate * dt));
    } else if (this.bigLeapActive && !this.onGround && this.jet !== "glide") {
      // BIG-LEAP coast: preserve the forward bound. WASD air-STEERS the arc's heading (toward wish) but
      // never bleeds the launch momentum — the target magnitude is max(targetSpeed, current speed), so a
      // giant bound carries through instead of the normal air-accel dragging it down to walk pace.
      if (moving) {
        const keep = Math.max(targetSpeed, this.vTan.length());
        this.tmp.copy(this.wish).multiplyScalar(keep).sub(this.vTan);
        const want = this.tmp.length();
        if (want > 1e-4) this.vTan.addScaledVector(this.tmp.normalize(), Math.min(want, AIR_ACCEL * dt));
      }
    } else if (moving && this.jet !== "glide") {
      // Ease the tangential velocity toward the wish direction.
      this.tmp.copy(this.wish).multiplyScalar(targetSpeed).sub(this.vTan);
      const want = this.tmp.length();
      if (want > 1e-4) this.vTan.addScaledVector(this.tmp.normalize(), Math.min(want, accel * dt));
    } else if (this.onGround && walkable) {
      this.vTan.multiplyScalar(Math.exp(-GROUND_FRICTION * dt));
      if (this.vTan.lengthSq() < 0.01) this.vTan.set(0, 0, 0);
    }
    this.vel.copy(this.vTan).add(this.vUp);

    // --- Jump chain + jetpack state machine ---
    // Tap 1 jumps, tap 2 double-jumps, tap 3 deploys flight. Holding cannot skip
    // a jump. Space resumes flight from hover; a short tap from flight selects
    // hover on release. Shift supplies thrust and releases back into glide.
    if(input.wasPressed('Space')&&!this.onGround&&!this.actionAirLock&&(this.jet==='hover'||this.flightDropped)&&this.energy>0){
      this.resumeHoverFlight();this.aimFlightSuppressed=aimActive;this.aiming=false;
    }else if(input.wasPressed('Space')&&!this.onGround&&!this.actionAirLock&&this.jet==='glide'){
      this.flightSpaceTap=true;this.flightSpaceT=0;
    }
    if(this.flightSpaceTap){
      if(this.onGround||this.actionAirLock||(this.jet!=='glide'&&this.jet!=='hover'))this.flightSpaceTap=false;
      else if(input.isDown('Space'))this.flightSpaceT+=dt;
      else{
        if(this.flightSpaceT<=.20){this.cancelDivePreparation();this.jet='hover';this.hoverHold=true;this.flightStalled=false;this.stallT=0;this.launchBoostT=0;}
        this.flightSpaceTap=false;
      }
    }
    this.simT += dt;
    this.coyote =
      (this.onGround && walkable) || this.sliding
        ? COYOTE_TIME
        : Math.max(0, this.coyote - dt);
    // Combat stance: Space DODGES on ANY directional move input — a 4-way evade-dash that travels
    // (forward/back/left/right), picked by the dominant axis — but JUMPS on NEUTRAL input. Only a real
    // dodge consumes the press; neutral falls through to the jump chain. main.ts plays the matching
    // dodge/roll from `pendingDodge`.
    let dodgedThisTick = false;
    // The evade-dash is a MELEE verb. A DIRECTIONAL Space in the melee stance is always dodge
    // intent: on cooldown the press is EATEN (user call: a mashed second directional roll must not
    // leak into a jump), never a jump. Neutral Space still falls through to the jump chain.
    //
    // ⚠ THE GUN-ADS DODGE IS REMOVED (user call 2026-08-03: "when armed with a rifle, moving +
    // space bar just sorta dashes in one direction — we should just jump normally in that
    // direction"). That was the `combatStance && aiming && !strafeLock` clause, and it meant a
    // rifle player could not jump while moving and aiming at all — every directional Space became
    // a dash. `evadeReady` is melee-gated by the action layer, so melee keeps its roll.
    if (!this.diveLandingLocked && !this.actionAirLock && this.evadeReady && input.wasPressed("Space")) {
      const md = this.moveDirLocal();
      const f = md.fwd, r = md.right;
      const mag = Math.hypot(f, r);
      if (mag > 0.3) {
        dodgedThisTick = true; // dodge intent — consumed whether or not the cooldown allows it
        if (this.dashCooldownT <= 0) {
          if (!this.onGround) {
            // AIR DODGE — no ROLLING mid-air (user call): a quick tangential impulse +
            // i-frames; the UAL Dodge_Left/Right one-shot rides it (pendingAirDodge →
            // main/lab → avatar.airDodge, side from the lateral input).
            this.tmp.copy(this.facing).multiplyScalar(f / mag).addScaledVector(this.right, r / mag);
            this.tmp.addScaledVector(this.up, -this.tmp.dot(this.up));
            if (this.tmp.lengthSq() > 1e-6) this.vel.addScaledVector(this.tmp.normalize(), AIR_DODGE_IMPULSE);
            this.iframeT = EVADE_IFRAMES;
            this.dashCooldownT = DASH_COOLDOWN;
            this.pendingAirDodge = r >= 0 ? "r" : "l";
          } else {
            // CROUCHED → the same profile over a LONGER window; STANDING → the standard roll.
            if (this.crouching) this.evade(md, DODGE_SPEED_START, CROUCH_ROLL_WINDOW);
            else this.evade(md);
            // 6-way pick: a BACK-DIAGONAL input rolls on the true SSH BL/BR clip; otherwise
            // the dominant axis picks the 4-way as before.
            this.pendingDodge =
              f < -0.35 && Math.abs(r) > 0.35
                ? (r >= 0 ? "br" : "bl")
                : Math.abs(r) >= Math.abs(f) ? (r >= 0 ? "r" : "l") : (f >= 0 ? "f" : "b");
          }
        }
      }
      // neutral → no dodge; fall through to the normal jump chain below
    }
    // JUMP-CANCEL is legal out of a dodge (DMC contract — jumping is itself an escape); only the
    // dive-landing pause still holds Space (the slam beat plays out before the next move).
    if (jumpPressed && !dodgedThisTick && this.diveLandPauseT <= 0 && !this.actionAirLock) {
      if (this.coyote > 0) {
        const canChargeSuperJump = SUPER_JUMP_ENABLED;
        const canChargeBoostLeap = SUPER_SPRINT_ENABLED && this.boosting && this.energy > BOOST_MIN_FUEL;
        if (!canChargeSuperJump && !canChargeBoostLeap) {
          // Jump on the press edge, independently of the Shift sprint input.
          this.vel.addScaledVector(this.up, JUMP_SPEED - this.vel.dot(this.up));
          this.jumpCount = 1;
          this.coyote = 0;
          this.onGround = false;
        } else {
          // PARKED IMPLEMENTATION: GROUND (incl. boost-running) holds to wind up
          // a charge; release chooses boost big-leap or vertical super-jump.
          this.chargingJump = true;
          this.spaceHeldT = 0;
          this.chargeBoost = canChargeBoostLeap;
        }
      } else if (
        JETPACK_ENABLED &&
        this.superLeap &&
        this.simT - this.doubleJumpAt < SUPER_FLIGHT_WINDOW &&
        this.jet !== "glide"
      ) {
        // ANY super-jump / big-leap → FLY: a further air-tap WITHIN A SHORT WINDOW after the launch
        // converts STRAIGHT to flight (glide) — the "tap Space again right after a big launch to fly" path.
        // Past the window the leap has committed to its arc, so a later tap falls through to the normal air
        // branches (re-engage hover / double-jump) instead of flying. (Holding A/S/D still picks the
        // air-strafe dodge instead.)
        this.thirdTapAir();
        this.superLeap = false;
      } else if (this.jet === 'none' && this.jumpCount <= 1) {
        this.doubleJump();
      } else if (JETPACK_ENABLED && this.jet === 'none' && this.jumpCount===2) {
        this.thirdTapAir();
      }
    }
    // Charge/release: hold Space on the ground to fill the meter, release to launch. A quick tap is the
    // base jump. Past JUMP_HOLD the charge fills and scales the launch. The flavor depends on chargeBoost
    // (latched at the press): boost-running → a forward BIG-LEAP; otherwise → the vertical SUPER-JUMP
    // (pops to JUMP_SPEED_MAX + the Takeoff_02 hover). Walking off the ledge cancels the charge.
    if (this.chargingJump) {
      if (this.coyote > 0 && input.isDown("Space") && !this.actionAirLock) {
        this.spaceHeldT += dt;
        this.jumpChargeFrac = Math.max(0, Math.min(1, (this.spaceHeldT - JUMP_HOLD) / JUMP_CHARGE));
      } else {
        if (this.coyote > 0) {
          const charged = this.spaceHeldT >= JUMP_HOLD && this.energy > BOOST_MIN_FUEL;
          // Boost-leap if we were boosting at the PRESS *or* are boost-sprinting NOW at the RELEASE. Boost
          // takes BOOST_RAMP (2.5s) to engage but a full charge hold is only ~1.75s, so a press that latched
          // chargeBoost=false can still finish ramping mid-charge — that must STILL fire the forward BIG-LEAP,
          // not the vertical super-jump (the player is boost-sprinting at the moment of the jump).
          const boostLeap = this.chargeBoost || (this.boosting && this.energy > BOOST_MIN_FUEL);
          if (boostLeap) {
            // BOOST BIG-LEAP: a giant forward BOUND in the run direction. The charge scales BOTH the
            // height and the forward distance. Ballistic (jet stays none) — it arcs back down to the
            // sprint-land clip, OR a further air-tap (superLeap) converts it to flight. Keeps sipping
            // boost fuel (BOOST_BURN) the whole arc (see the airborne fuel block below).
            const t = charged ? this.jumpChargeFrac : 0;
            const up = BOOST_LEAP_UP + (BOOST_LEAP_UP_MAX - BOOST_LEAP_UP) * t;
            const fwd = BOOST_LEAP_FWD + (BOOST_LEAP_FWD_MAX - BOOST_LEAP_FWD) * t;
            this.vel.addScaledVector(this.up, up - this.vel.dot(this.up)); // set the vertical launch
            this.vel.addScaledVector(this.heading, fwd); // forward bound, ADDED to the run momentum
            this.jumpCount = 1; // a leap; the next air-tap flies (superLeap)
            this.jet = "none";
            this.superLeap = true;
            this.bigLeapActive = true;
            this.bigLeapPending = true; // → avatar: sprint-start → loop → sprint-land
            this.doubleJumpAt = this.simT;
          } else if (charged) {
            // SUPER-JUMP (vertical): pop to JUMP_SPEED_MAX + briefly engage the jetpack hover (Takeoff_01
            // upright → climb → hover). A further air-tap (superLeap) converts to flight at ANY time. It
            // goes essentially STRAIGHT UP — damp the horizontal momentum so a running charge launches
            // vertically (a little forward carry remains) instead of arcing away.
            const v = JUMP_SPEED + (JUMP_SPEED_MAX - JUMP_SPEED) * this.jumpChargeFrac;
            this.vUp.copy(this.up).multiplyScalar(this.vel.dot(this.up));
            this.vTan.copy(this.vel).sub(this.vUp).multiplyScalar(0.25); // keep ~25% of the run momentum
            this.vel.copy(this.vTan).addScaledVector(this.up, v);
            this.jumpCount = 2; // lands in the hover/air state (a further tap → glide)
            this.jet = "hover";
            this.jetGrace = SUPER_HOVER_GRACE; // keep the hover alive through the Space-release
            this.doubleJumpAt = this.simT;
            this.superLeap = true;
          } else {
            this.vel.addScaledVector(this.up, JUMP_SPEED - this.vel.dot(this.up));
            this.jumpCount = 1;
          }
          this.coyote = 0;
          this.onGround = false;
        }
        this.chargingJump = false;
        this.spaceHeldT = 0;
        this.jumpChargeFrac = 0;
        this.chargeBoost = false;
      }
    }
    this.jetGrace = Math.max(0, this.jetGrace - dt);
    // One press gets one preparation. A held key cannot repeatedly pause the
    // player after mouse look cancels it. Ordinary steep-look dives stay immediate.
    let diveCancelled=false;
    if(this.divePrepT>0&&(!diveHeld||this.onGround||this.actionAirLock||this.jet!=='glide'
      ||(this.pitch<AIM_DIVE.enterPitch&&(this.diveLookDelta<-.002||this.pitch<this.diveGuidedPitch-.01)))){
      this.cancelDivePreparation();diveCancelled=true;
    }
    if(JETPACK_ENABLED&&this.energy>0&&divePressed&&!(this.aimHover&&aimPressed)&&this.jet!=='dive'&&!this.onGround&&!this.actionAirLock&&this.groundClearance>DIVE_MIN_HEIGHT){
      // X breaks out of an established aiming hover just as Shift does: a
      // still-held RMB must not pull the player back into ADS until it is
      // pressed again. Pressed on the same frame, RMB still wins.
      if(this.aimHover){this.aimHover=false;this.aimFlightSuppressed=true;this.aimFlightCarry=0;}
      this.hoverHold=false;
      this.diveReturnJet=this.jet;this.divePrepT=DIVE_PREPARATION.time;
      this.diveStartPitch=this.diveGuidedPitch=this.pitch;
      if(this.vel.lengthSq()>1e-6)this.diveStartDir.copy(this.vel).normalize();else this.diveStartDir.copy(this.heading);
      this.launchBoostT=0;this.aimDiving=false;this.flightPower=0;
      this.jet='glide';this.aiming=false;this.flightStalled=false;this.stallT=0;this.glideApproach.amount=0;
    }else if(divePressed&&!(this.aimHover&&aimPressed)&&(this.jet==='hover'||this.jet==='glide')&&this.divePrepT<=0
      &&!this.onGround&&!this.actionAirLock){
      // Too low (or too empty) for a plunge: X still means "down" — the
      // flight ends and the pilot drops. Space/Shift pick flight back up.
      this.dropFlight();
    }
    const aimDiveEligible=!this.aimHover&&!this.hoverHold&&this.aimFlightRelease<=0&&!this.onGround&&!this.actionAirLock&&this.jet!=='none'&&this.launchBoostT<=0&&this.divePrepT<=0&&!diveCancelled;
    this.aimDiving=aimDiveEligible&&this.pitch>=(this.aimDiving?AIM_DIVE.exitPitch:AIM_DIVE.enterPitch);
    const wantDive=this.aimDiving;
    if(this.jet==='dive'){
      if(!wantDive){
        this.jet=this.vel.length()>=GLIDE.recoverSpeed?'glide':'hover';
        this.flightStalled=this.jet==='hover';this.stallT=0;
      }
    }else if(JETPACK_ENABLED&&wantDive&&!this.onGround&&!this.actionAirLock&&this.groundClearance>DIVE_MIN_HEIGHT){
      this.jet='dive';this.flightStalled=false;this.jumpCount=3;
    }
    if(this.aiming&&this.heldItemId)this.weaponReleaseT=weaponStats(ITEMS[this.heldItemId]?.weapon??{}).stowTime;
    else this.weaponReleaseT=Math.max(0,this.weaponReleaseT-dt);
    const mobility=this.weaponMobility;
    if(mobility===0&&this.jet!=='none')this.dropFlight();
    const flightCost=weaponFlightCost(mobility);
    const gravity=GRAVITY*sizeG(planet.def.radius);
    const canThrust=!this.aimHover&&!this.hoverHold&&!this.onGround&&!this.actionAirLock&&this.divePrepT<=0&&(this.jet==='glide'||this.jet==='hover');
    if(!canThrust)this.launchBoostT=0;
    const wasGrounded=this.onGround;
    let flightDt=dt;
    const diveGathering=this.divePrepT>0;
    while(flightDt>1e-9&&this.divePrepT>0){
      const brakeLeft=this.divePrepT-(DIVE_PREPARATION.time-DIVE_PREPARATION.brake);
      const h=Math.min(flightDt,1/120,this.divePrepT,brakeLeft>1e-9?brakeLeft:Infinity);
      const next=Math.max(0,this.divePrepT-h);
      // Keep the (smoothly braked) speed, but swing its direction from the
      // X-press heading toward straight down with the body's pitch-over.
      const speed=this.vel.length()*divePreparationRetention(next)/divePreparationRetention(this.divePrepT);
      this.tmp.copy(this.up).negate();
      this.diveTurnFull.setFromUnitVectors(this.diveStartDir,this.tmp);
      this.diveTurn.identity().slerp(this.diveTurnFull,divePreparationTurn(next));
      this.vel.copy(this.diveStartDir).applyQuaternion(this.diveTurn).multiplyScalar(speed);
      this.divePrepT=next<1e-9?0:next;flightDt=Math.max(0,flightDt-h);
      const look=THREE.MathUtils.smootherstep(1-this.divePrepT/DIVE_PREPARATION.time,0,1);
      this.pitch=Math.max(this.pitch,THREE.MathUtils.lerp(this.diveStartPitch,DIVE_PREPARATION.pitch,look));
      this.diveGuidedPitch=this.pitch;
      this.advanceSolids(h,planet,curMag>0,ledgeAllowed&&!input.isDown('KeyS'));
      if(this.ledgeClimb.state){this.ledgeDiveHeld=ledgeDive;this.applyLedgeMotion('climb',dt,planet);return;}
      this.clampToGround(planet,false);
      if(this.onGround){this.resetFlight();break;}
    }
    if(diveGathering&&this.divePrepT===0&&!this.onGround){
      if(this.pitch>=AIM_DIVE.enterPitch){
        const retainedSpeed=this.vel.length();
        this.vel.copy(this.up).multiplyScalar(-retainedSpeed);
        this.jet='dive';this.aimDiving=true;this.jumpCount=3;
      }else this.jet=this.diveReturnJet;
    }
    // Braking is a profile, not repeated percentage loss. Walk it at the same
    // 120 Hz cadence as flight, retaining swept wall/ledge/ground collision.
    // Only the clock after this hang can generate thrust or spend fuel.
    const gathering=this.launchBoostT>FLIGHT_LAUNCH_TIME+1e-9;
    while(flightDt>1e-9&&this.launchBoostT>FLIGHT_LAUNCH_TIME+1e-9){
      const brakeLeft=this.launchBoostT-(FLIGHT_LAUNCH_TOTAL-FLIGHT_LAUNCH_BRAKE);
      const h=Math.min(flightDt,1/120,this.launchBoostT-FLIGHT_LAUNCH_TIME,brakeLeft>1e-9?brakeLeft:Infinity);
      const next=Math.max(FLIGHT_LAUNCH_TIME,this.launchBoostT-h);
      this.vel.multiplyScalar(flightLaunchRetention(next)/flightLaunchRetention(this.launchBoostT));
      this.launchBoostT=next;flightDt=Math.max(0,flightDt-h);
      this.advanceSolids(h,planet,curMag>0,ledgeAllowed&&!input.isDown('KeyS'));
      if(this.ledgeClimb.state){this.ledgeDiveHeld=ledgeDive;this.applyLedgeMotion('climb',dt,planet);return;}
      this.clampToGround(planet,false);
      if(this.onGround){this.resetFlight();break;}
    }
    if(flightDt<1e-9)flightDt=0;
    if(gathering&&this.launchBoostT>0&&this.launchBoostT<=FLIGHT_LAUNCH_TIME+1e-9){
      this.launchBoostT=FLIGHT_LAUNCH_TIME;
      const retainedSpeed=this.vel.length();
      this.vel.copy(this.heading).multiplyScalar(Math.cos(this.pitch))
        .addScaledVector(this.up,-Math.sin(this.pitch)).multiplyScalar(retainedSpeed);
    }
    // Split the expiry tick: holding Shift during launch spends fuel only for
    // the portion after the free burst, independent of simulation frequency.
    const freeTime=this.launchBoostT<=FLIGHT_LAUNCH_TIME+1e-9?Math.min(flightDt,this.launchBoostT):0;
    if(this.launchBoostT<=FLIGHT_LAUNCH_TIME+1e-9)this.launchBoostT=Math.max(0,this.launchBoostT-flightDt);
    if(this.launchBoostT<1e-9)this.launchBoostT=0;
    const freePower=flightDt>1e-9?freeTime/flightDt:0;
    if(this.jet!=='glide'||this.divePrepT>0||diveGathering||this.actionAirLock||this.onGround)this.launchRecovery.reset();
    const recovery=this.launchRecovery.update(flightDt,freeTime,freeTime>0&&this.launchBoostT===0&&this.jet==='glide'&&!this.onGround);
    const cost=GLIDE.boostBurn*this.burnMul*flightCost*Math.max(0,flightDt-freeTime);
    const paidPower=shift&&canThrust&&!this.onGround&&cost>0?Math.min(1,this.energy/cost):0;
    const power=freePower+(1-freePower)*paidPower;
    const thrustPower=freePower*FLIGHT_LAUNCH_THRUST+(1-freePower)*paidPower;
    this.flightPower=dt>0?power*flightDt/dt:0;
    if(paidPower>0)this.burnEnergy(cost*paidPower);
    this.dropEmptyFlight();
    if(this.jet==='glide'){
      // Release the remaining pre-aim momentum into the new sight direction.
      // This short handoff grants no launch burst and cannot refresh its reserve.
      if(this.aimFlightRelease>0){
        const h=Math.min(flightDt,this.aimFlightRelease);
        this.aimFlightCarry*=Math.exp(-.65*h);
        this.tmp.copy(this.heading).multiplyScalar(Math.cos(this.pitch)).addScaledVector(this.up,-Math.sin(this.pitch));
        this.tmp.multiplyScalar(Math.max(this.vel.length(),this.aimFlightCarry));
        this.vel.lerp(this.tmp,1-Math.exp(-18*h));
        this.aimFlightRelease=Math.max(0,this.aimFlightRelease-h);
      }
      // The short free release follows the chosen aim without sagging while
      // it builds speed. Partial expiry ticks restore only their normal share.
      this.glidePhysics.step(this.vel,this.up,this.heading,this.pitch,
        (input.isDown('KeyD')?1:0)-(input.isDown('KeyA')?1:0),input.isDown('KeyS'),gravity*(1-freePower),power,flightDt,thrustPower,recovery);
      this.stallT=this.aimFlightRelease<=0&&!gathering&&!diveGathering&&freePower===0&&this.vel.length()<GLIDE.stallSpeed?this.stallT+flightDt:0;
      if(this.stallT>=GLIDE.stallDelay){this.jet='hover';this.flightStalled=true;this.stallT=0;}
    }else if(this.jet==='hover'){
      // Hover catches vertical momentum smoothly. Empty reserves leave the
      // hover state entirely; never draw emergency combat shield fuel.
      const steps=Math.max(1,Math.ceil(flightDt*120)),h=flightDt/steps;
      for(let i=0;i<steps;i++){
        const hoverCost=GLIDE.boostBurn*HOVER.burnFraction*this.burnMul*flightCost*h;
        const support=power>0?power:hoverCost>0?Math.min(1,this.energy/hoverCost):0;
        if(power<=0&&support>0)this.burnEnergy(hoverCost*support);
        const rise=this.vel.dot(this.up);
        this.vel.addScaledVector(this.up,hoverVerticalVelocity(rise,gravity,support,h)-rise);
      }
      if(power>0){
        // A short upward bias lets a stalled pilot recover without losing the new speed.
        const pitch=Math.min(this.pitch,.15);
        this.tmp.copy(this.heading).multiplyScalar(Math.cos(pitch)).addScaledVector(this.up,-Math.sin(pitch));
        this.vel.addScaledVector(this.tmp,GLIDE.thrust*thrustPower*flightDt);
        if(this.vel.length()>=GLIDE.recoverSpeed){this.jet='glide';this.flightStalled=false;this.stallT=0;}
      }
    }else if(this.jet==='dive'){
      const curUp=this.vel.dot(this.up);
      let down=diveFallSpeed(-curUp,gravity,flightDt);
      // Touchdown flare: follow the brake curve to the floor below, easing in
      // (never more than twice its rate) if a platform appears inside it.
      const allowed=diveBrakeSpeed(this.diveFloorClearance);
      if(down>allowed)down=Math.max(allowed,-curUp-2*DIVE_BRAKE.decel*flightDt);
      this.vel.addScaledVector(this.up,-down-curUp);
    }else{
      const gK=this.raveFloat&&this.raveLiftLeft&&!this.onGround&&this.vel.dot(this.up)<0?RAVE_FALL_G:1;
      this.vel.addScaledVector(this.up,-gravity*gK*flightDt);
      const vUp=this.vel.dot(this.up);
      if(vUp<-TERMINAL_FALL)this.vel.addScaledVector(this.up,-TERMINAL_FALL-vUp);
    }
    if (this.stingerActive) {
      // Meet the target's height along a bounded ballistic approach. Tangent-only
      // steering left floating enemies above the slash even after a good leap.
      const remaining = Math.max(.12, (this.stingerDist - 2.3) / 24);
      const rise = Math.max(-20, Math.min(20, this.stingerHeight / remaining + gravity * remaining * .5));
      this.vel.addScaledVector(this.up, rise - this.vel.dot(this.up));
    }

    this.dropEmptyFlight();

    // Only coasting glide prepares a landing. Hover's supported sink is still
    // flight: proximity alone must not plant its feet on an invisible floor.
    // update(false) smoothly releases a previous glide approach on hover entry.
    const approachEnabled=!this.aimHover&&!gathering&&!diveGathering&&!this.onGround&&!this.actionAirLock&&power===0&&this.pitch>-.15
      &&this.jet==='glide';
    const approachRise=this.vel.dot(this.up);
    this.glideApproach.update(dt,this.groundClearance,approachRise,
      Math.sqrt(Math.max(0,this.vel.lengthSq()-approachRise*approachRise)),approachEnabled);
    if(approachEnabled)this.glideApproach.brake(this.vel,this.up,dt);

    // --- Integrate + ground clamp ---
    const fallSpeed = -this.vel.dot(this.up); // pre-clamp, for fall damage
    // SUB-STEPPED against solids (see SOLID_STEP_M): the whole frame's displacement,
    // walked in slices short enough that the push-out can still win, with walls
    // consulted after every one. Identical to a single add + resolve whenever the
    // slice count is 1, which is every ordinary tick.
    this.advanceSolids(flightDt,planet,curMag>0,ledgeAllowed&&!input.isDown('KeyS'));
    if(this.ledgeClimb.state){
      this.ledgeDiveHeld=ledgeDive;this.applyLedgeMotion('climb',dt,planet);return;
    }
    this.clampToGround(planet, wasGrounded && this.vel.dot(this.up) <= JUMP_SPEED * 0.5);
    this.impactSpeed = !wasGrounded && this.onGround ? Math.max(0, fallSpeed) : 0;

    // Touch-down: jets off, jump chain reset, fuel trickles back. The big-leap / fly-on-next-tap state
    // ends here (back on the ground — the leap is over, the chain is fresh).
    if (this.onGround) {
      if(!wasGrounded&&this.jet!=='dive'&&!dashing&&!this.stingerActive&&!this.actionAirLock){
        // Feet have planted: discard airborne carry instead of spending a
        // second braking it across the floor. Held input resumes ground pace
        // immediately; neutral input plants in place without a movement lock.
        this.vel.copy(this.wish).multiplyScalar(moving?targetSpeed:0);
      }
      // A DIVE that reaches the ground lands SAFELY: zero the impact (no fall damage — you meant to
      // plunge), flag the SuperHero flight-land flourish (main.ts → avatar.flagDiveLand), and root the
      // body for the slam beat (DIVE_LAND_PAUSE) so the flourish plays out.
      if (this.jet === "dive") {
        this.impactSpeed = 0;
        this.diveLandPending = true;
        this.diveLandPauseT = DIVE_LAND_PAUSE;
        this.vel.set(0,0,0);this.dashT=0;this.dashSpeed=0;this.strikeStepActive=false;this.stingerActive=false;
      }
      this.jet = "none";
      this.resetFlight();
      this.jumpCount = 0;
      this.superLeap = false;
      this.bigLeapActive = false;
      this.groundedFor += dt;
      if (this.boosting) {
        this.burnEnergy(BOOST_BURN * this.burnMul * dt); // boost-run sips energy (no regen while boosting)
      } else if (this.groundedFor > FUEL_REGEN_DELAY && this.canRegen()) {
        this.energy = Math.min(this.energyMax, this.energy + this.regenGround * this.regenMul * this.regenGate * dt);
      }
    } else {
      this.groundedFor = 0;
      // A BIG-LEAP keeps sipping boost fuel (BOOST_BURN) the whole arc — "same fuel rate" as the boost
      // run it launched from. Touching solid ground is the only flight recharge.
      if (this.bigLeapActive && this.jet === "none") this.burnEnergy(BOOST_BURN * this.burnMul * dt);
      // Airborne energy never regenerates, including freefall and stalled hover.
    }

    this.groundSpeed = this.vTan.copy(this.vel).addScaledVector(this.up, -this.vel.dot(this.up)).length();

    // --- Visual orientation. Glide follows carried velocity; aim and hover follow the camera
    // (hover can strafe using directional flight_left/right/back clips).
    // Walking on the ground, not aiming, follows the tangential MOVEMENT direction,
    // but ONLY while there's real horizontal speed (groundSpeed > 0.5), so standing still just holds
    // the current facing instead of swiveling. ---
    if(this.diveLandingLocked){
      // Keep the planted landing pose facing its impact heading as the camera moves.
    } else if (this.onGround && this.strikeControlT > 0 && this.facingHold) {
      // A blade swing holds its line: the hit window (and an unaimed swing
      // throughout) does not turn with the camera or the stick.
    } else if (!this.onGround && !this.stingerActive && (this.airFocusOn || this.airOrbitT > 0)) {
      // AIRBORNE + LOCKED keeps the eyes ON THE TARGET (user call 2026-07-24): the air evade
      // circles the lock, so the body must FACE the centre of that circle — a strafe around
      // them, not a blind sideways drift — and it must keep facing them for the WHOLE airborne
      // stint, not just the orbit window. Straight at the target, NOT via the camera heading:
      // the heading's lock magnetism is rate-capped, so a fast circle out-runs it. Beats the
      // aim/strafe-lock branch below; the STINGER is exempt (its leap owns facing).
      this.tmp.copy(this.airFocusOn ? this.airFocusPos : this.airOrbitCenter).sub(this.pos);
      this.tmp.addScaledVector(this.up, -this.tmp.dot(this.up));
      if (this.tmp.lengthSq() > 1e-6) {
        this.tmp.normalize();
        const cos = Math.max(-1, Math.min(1, this.facing.dot(this.tmp)));
        const ang = Math.acos(cos);
        if (ang > 1e-3) {
          const step = ang * (1 - Math.exp(-18 * dt));
          this.tmpB.crossVectors(this.facing, this.tmp);
          const sign = this.tmpB.lengthSq() < 1e-8 ? 1 : (this.tmpB.dot(this.up) >= 0 ? 1 : -1);
          this.facing.applyAxisAngle(this.up, sign * step);
        }
      }
    } else if(this.jet==='glide'){
      if(this.launchBoostT>=FLIGHT_LAUNCH_TIME)this.tmp.copy(this.heading);
      else this.tmp.copy(this.vel).addScaledVector(this.up,-this.vel.dot(this.up));
      // The flight path already eases into the steering input. Follow it at the
      // old flight-facing rate so body rotation does not add a second slow lag.
      if(this.tmp.lengthSq()>.01)this.facing.lerp(this.tmp.normalize(),1-Math.exp(-16*dt));
    } else if (this.poseState().facing === "aim" || this.strafeLock || this.jet !== "none") {
      // ...incl. the SWORD AIR ATTACK charge: face the camera so the player aims the plunge with the mouse.
      // The DIVE is camera-locked too, so its WASD drift reads as a STRAFE (banking anims pick the side).
      // strafeLock = the sticky target lock / 2H guard: same facing rule without the aim hold.
      // ⚠ CROUCH IS NOT IN THIS LIST (tried 2026-08-10, reverted on the user's call): the octagonal
      // crouch strafe must only engage while RMB is held, exactly as the gun stances do. `aiming`
      // covers it — unarmed RMB sets it too (main.ts walkMode) — so a crouched body camera-locks
      // and strafes on RMB, and otherwise faces its velocity and walks the crouch forward.
      this.facing.lerp(this.heading, 1 - Math.exp(-16 * dt));
    } else if (this.wish.lengthSq() > 0.2 || this.groundSpeed > 0.5) {
      // FACE THE INTENT, angularly (user report 2026-07-23: quick turnarounds had a weird
      // lag). The old form chased the VELOCITY direction with a vector LERP — two stacked
      // lags on a 180°: velocity takes ~0.4 s to reverse under ground accel (facing waits
      // for it), and a near-antiparallel lerp DEGENERATES (the vector shrinks through
      // zero, stalls, then snaps after the normalize). Instead: rotate about `up` toward
      // the WISH direction (the input — it flips the same frame the keys do) at a
      // proportional angular rate. No degeneracy at any angle; the turn starts instantly.
      this.tmp.copy(this.wish.lengthSq() > 0.2 ? this.wish : this.vel);
      this.tmp.addScaledVector(this.up, -this.tmp.dot(this.up));
      if (this.tmp.lengthSq() > 1e-6) {
        this.tmp.normalize();
        const cos = Math.max(-1, Math.min(1, this.facing.dot(this.tmp)));
        const ang = Math.acos(cos);
        if (ang > 1e-3) {
          const step = ang * (1 - Math.exp(-12 * dt));
          this.tmpB.crossVectors(this.facing, this.tmp);
          // Exactly-antiparallel input has no cross axis — pick a side deterministically.
          const sign = this.tmpB.lengthSq() < 1e-8 ? 1 : (this.tmpB.dot(this.up) >= 0 ? 1 : -1);
          this.facing.applyAxisAngle(this.up, sign * step);
        }
      }
    }
    this.facing.addScaledVector(this.up, -this.facing.dot(this.up));
    if (this.facing.lengthSq() < 1e-6) this.facing.copy(this.heading);
    this.facing.normalize();
    this.updateQuat();
  }

  /**
   * Buoyant SWIM integration — replaces the whole land/jump/gravity/clamp path
   * while in deep water. The body floats at the surface (a damped buoyancy
   * spring); W/A/S/D swim horizontally (camera-relative tangent); Space rises,
   * X/C dives; the river current carries you. `r` is the current radius and
   * `this._wc` holds the freshly-sampled water column (seabed + flow).
   */
  private swimUpdate(dt: number, input: Input, planet: PlanetHost, r: number, curMag: number): void {
    this.wallRun.reset();this.ledgeClimb.reset();
    this.jet = "none";
    this.resetFlight();
    this.jumpCount = 0;
    this.boosting = false;
    this.boostT = 0;
    this.crouching = false;
    this.sliding = false;
    // The float line: the LOCAL water surface (river ws inland, sea elsewhere) —
    // the column was freshly sampled by update() just before it dispatched here.
    const seaR = this._wc.surfR > -1e8 ? this._wc.surfR : (planet.def.seaRadius ?? r);
    const moving = this.wish.lengthSq() > 0;
    // Shift = sprint-swim: the faster front-crawl gear (the avatar reads `sprinting`).
    const shift = input.isDown("ShiftLeft") || input.isDown("ShiftRight");
    this.sprinting = moving && shift;
    const speed = this.sprinting ? SWIM_SPRINT_SPEED : SWIM_SPEED;

    // LEAP OUT: a Space TAP near the surface launches the swimmer into the air with
    // the full air kit. jumpCount=1 (a "first jump" was spent) means the next air-tap
    // is the double-jump → hover → glide flight — identical to a ground jump. The grace
    // (set above) keeps the state airborne so buoyancy + the surface clamp can't recapture.
    if (input.wasPressed("Space") && seaR - r < SWIM_SUBMERGE + 0.9) {
      this.vel.addScaledVector(this.up, WATER_LEAP_SPEED - this.vel.dot(this.up));
      this.jumpCount = 1;
      this.waterLeapGrace = WATER_LEAP_GRACE;
      this.onGround = false;
      this.coyote = 0;
      this.advanceSolids(dt, planet); // includes the underside of finite natural ledges
      this.groundClearance = 0;
      this.impactSpeed = 0;
      this.groundSpeed = this.tmp.copy(this.vel).addScaledVector(this.up, -this.vel.dot(this.up)).length();
      return;
    }

    // Horizontal: ease the tangential velocity toward the swim wish.
    this.vUp.copy(this.up).multiplyScalar(this.vel.dot(this.up));
    this.vTan.copy(this.vel).sub(this.vUp);
    if (moving) {
      this.tmp.copy(this.wish).multiplyScalar(speed).sub(this.vTan);
      const want = this.tmp.length();
      if (want > 1e-4) this.vTan.addScaledVector(this.tmp.normalize(), Math.min(want, SWIM_ACCEL * dt));
    } else {
      this.vTan.multiplyScalar(Math.exp(-SWIM_DRAG * dt));
    }

    // Vertical: dive/surface input, else a damped buoyancy spring to the float line.
    let vR = this.vel.dot(this.up);
    if (input.isDown("Space")) vR += SWIM_VERT_ACCEL * dt;
    else if (input.isDown("KeyX") || input.isDown("KeyC")) vR -= SWIM_VERT_ACCEL * dt;
    else {
      vR += (seaR - SWIM_SUBMERGE - r) * BUOYANCY * dt; // spring toward the surface
      vR *= Math.exp(-SWIM_DRAG * dt); // damping → a gentle bob, no overshoot
    }
    vR = Math.max(-SWIM_VERT_MAX, Math.min(SWIM_VERT_MAX, vR));
    this.vUp.copy(this.up).multiplyScalar(vR);
    this.vel.copy(this.vTan).add(this.vUp);

    // Integrate + current drift.
    this.advanceSolids(dt, planet, curMag > 0);

    // Keep the body between the seabed and just breaching the surface.
    this.dir.copy(this.pos).sub(this.center);
    const rNew = this.dir.length() || 1;
    this.dir.divideScalar(rNew);
    const minR = this._wc.floorR + 0.25;
    const maxR = seaR + 0.4;
    if (rNew < minR) {
      this.pos.copy(this.center).addScaledVector(this.dir, minR);
      const vn = this.vel.dot(this.dir); if (vn < 0) this.vel.addScaledVector(this.dir, -vn);
    } else if (rNew > maxR) {
      this.pos.copy(this.center).addScaledVector(this.dir, maxR);
      const vn = this.vel.dot(this.dir); if (vn > 0) this.vel.addScaledVector(this.dir, -vn);
    }

    this.onGround = false;
    this.groundClearance = 0;
    this.impactSpeed = 0;
    this.groundSpeed = this.tmp.copy(this.vel).addScaledVector(this.up, -this.vel.dot(this.up)).length();

    // Face the swim direction when moving (else hold); aim faces the camera.
    if (this.aiming) {
      this.facing.lerp(this.heading, 1 - Math.exp(-16 * dt));
    } else if (this.groundSpeed > 0.4) {
      this.tmp.copy(this.vel).addScaledVector(this.up, -this.vel.dot(this.up)).normalize();
      this.facing.lerp(this.tmp, 1 - Math.exp(-8 * dt));
    }
    this.facing.addScaledVector(this.up, -this.facing.dot(this.up));
    if (this.facing.lengthSq() < 1e-6) this.facing.copy(this.heading);
    this.facing.normalize();
    this.updateQuat();
  }

  /**
   * Clamp the feet to the analytic surface (wading stands on the real seabed).
   * With `snap`, also glues down within SNAP_DIST so downhill walking
   * doesn't flicker between grounded and airborne every stride.
   */
  private clampToGround(planet: PlanetHost, snap: boolean): void {
    this.sliding = false;
    this.dir.copy(this.pos).sub(this.center);
    const r = this.dir.length() || 1;
    this.dir.divideScalar(r);
    // Stand on the REAL seabed (wading); deep water never reaches here — the swim
    // branch in update() owns it (no more artificial sea-floor clamp).
    let ground = planet.surfaceRadiusWorld(this.dir);
    // A dive reads the floor it is heading for, however far below: the
    // ordinary catch window only sees roofs within one step of the feet.
    if (this.jet === 'dive') {
      const below = this.roofStand(planet, r);
      this.diveFloorClearance = Math.max(0, r - Math.max(ground, below ?? -Infinity));
    } else this.diveFloorClearance = Infinity;
    // Roof standing: building tops are walkable floors. Take the roof when
    // the feet are at/above the roofline — never while beside the wall
    // below it, where the lateral push (resolveSettlementWalls) owns the contact.
    // The catch window grows with fall speed: a fixed 0.6 m is smaller than
    // one terminal-velocity step (~0.87 m/frame), which tunneled straight
    // through the roof plane on hard drops (review-caught).
    const fallStep = Math.max(0, -this.vel.dot(this.dir)) / 60 + 0.6;
    const stand = this.roofStand(planet, r + fallStep);
    if (stand !== null && stand > ground) {
      if (r > stand - fallStep) ground = stand;
    }
    // Swept collision already prevents tunnelling; only a tiny upward tolerance
    // is needed here. A fall-speed-sized upward ray could select an overhead roof.
    const cliff = this.terrainContacts.support(planet, this.pos, .06, Math.max(2, fallStep));
    const cliffGround = cliff !== null && cliff > ground ? cliff : null;
    if (cliffGround !== null) ground = cliffGround;
    // Interiors P2: inside a building shell the raised floor slab is the
    // AUTHORITATIVE ground — unconditionally, no catch window. It cannot
    // arrive via roofStand: standRadius reports the HIGHEST standable top
    // (the roof), which the fall window rightly rejects from inside, leaving
    // feet on the terrain that may dip BELOW the slab (the knee-sink bug).
    // (Duck-typed: PlanetHost declares the base BoxColliderField — pad worlds
    // hang plain fields there — while real planets provide SettlementColliders,
    // which carries the interiors extension.)
    const sc = planet.settlementColliders as
      (typeof planet.settlementColliders & { interiorFloorRadiusAt?: (x: number, y: number, z: number) => number | null }) | null;
    if (planet.sampler && planet.pose.invQuat && sc?.interiorFloorRadiusAt) {
      this._twTmp.copy(this.pos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
      const ifr = sc.interiorFloorRadiusAt(this._twTmp.x, this._twTmp.y, this._twTmp.z);
      if (ifr !== null && ifr > ground) ground = ifr;
    }
    this.groundClearance = Math.max(0, r - ground); // feet-above-surface (pre-clamp) — drives land anticipation
    // A dive anticipates the floor it will actually meet, platforms included.
    if (this.jet === 'dive') this.groundClearance = Math.min(this.groundClearance, this.diveFloorClearance);

    if (r <= ground + 0.02 || (snap && r < ground + SNAP_DIST)) {
      this.pos.copy(this.center).addScaledVector(this.dir, ground);
      // Steep ground sheds velocity along the surface NORMAL (leaving a
      // downhill component — the slide); walkable ground sheds radial.
      planet.surfaceNormalWorld(this.dir, this.normalArr, SLOPE_EPS);
      if (cliffGround !== null && ground === cliffGround) this.terrainContacts.normal.toArray(this.normalArr);
      const slopeCos =
        this.normalArr[0] * this.dir.x + this.normalArr[1] * this.dir.y + this.normalArr[2] * this.dir.z;
      if (slopeCos > WALKABLE_COS) {
        this.tmp.set(this.normalArr[0], this.normalArr[1], this.normalArr[2]);
        const contactNormal = cliffGround !== null && ground === cliffGround ? this.tmp : this.dir;
        const vn = this.vel.dot(contactNormal);
        if (vn < 0) this.vel.addScaledVector(contactNormal, -vn);
        this.onGround = true;
        this.airHopN = 0; // touchdown ends the air string — the hop ladder starts over
      } else {
        this.tmp.set(this.normalArr[0], this.normalArr[1], this.normalArr[2]);
        const vn = this.vel.dot(this.tmp);
        if (vn < 0) this.vel.addScaledVector(this.tmp, -vn);
        this.onGround = false; // sliding: no friction (jump stays available)
        this.sliding = true;
      }
    } else {
      this.onGround = false;
    }
  }

  private _twP: [number, number, number] = [0, 0, 0];
  private _twTmp = new THREE.Vector3();
  private _scOut = { x: 0, y: 0, z: 0 };
  private terrainContacts = new TerrainContacts();
  private solidPrevious = new THREE.Vector3();

  /** Highest walkable roof under the current position as a WORLD radius (null off-roof). */
  /** `maxR` (interiors P3): the catch ceiling (feet radius + fall window).
   *  The settlement query is height-capped so an interior stair tread or
   *  mezzanine grounds even though the ROOF is the global highest standable
   *  above it (the plain max + window gate rejected everything under an
   *  overhang). Scatter/homestead hooks are already height-aware probes. */
  private roofStand(planet: PlanetHost, maxR = Infinity): number | null {
    // Headless tests drive a minimal planet stub.
    if (!planet.sampler || !planet.pose.invQuat) return null;
    this._twTmp.copy(this.pos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
    this._twP[0] = this._twTmp.x;
    this._twP[1] = this._twTmp.y;
    this._twP[2] = this._twTmp.z;
    const roof = planet.settlementColliders?.standRadiusBelow(this._twP[0], this._twP[1], this._twP[2], maxR) ?? null;
    // Boulders are climbable domes — same walkable-floor treatment as roofs.
    const rock = planet.scatterStandRadius
      ? planet.scatterStandRadius(this._twP[0], this._twP[1], this._twP[2])
      : null;
    // Homestead pieces: player-built floors/foundations/stairs are floors too.
    const piece = planet.homesteadStandRadius
      ? planet.homesteadStandRadius(this._twP[0], this._twP[1], this._twP[2])
      : null;
    let best = roof;
    if (rock !== null && (best === null || rock > best)) best = rock;
    if (piece !== null && (best === null || piece > best)) best = piece;
    return best;
  }

  /** Both ordinary and swept flight catches hand ownership to the same climb. */
  private applyLedgeMotion(ledge:'climb'|'land',dt:number,planet:PlanetHost):void {
    this.wallRun.release();this.jet='none';this.resetFlight();
    this.onGround=ledge==='land';this.jumpCount=this.onGround?0:1;this.coyote=0;this.groundedFor=0;
    this.doubleJumpPending=false;this.superLeap=false;this.bigLeapActive=false;this.chargingJump=false;
    this.sprinting=false;this.boosting=false;this.boostT=0;this.walkMode=false;
    this.facing.lerp(this.ledgeClimb.forward,1-Math.exp(-22*dt)).normalize();this.updateQuat();
    this.ledgeAnchor.copy(this.ledgeClimb.anchor).sub(this.pos).applyQuaternion(this.quat.clone().invert());
    this.groundSpeed=0;this.impactSpeed=0;this.groundClearance=this.onGround?0:1;
    if(this.onGround)this.clampToGround(planet,true);
  }

  /** Substep against the same settlement/construction solids that are drawn. */
  private advanceSolids(dt:number,planet:PlanetHost,current=false,catchFlightLedge=false):void {
    this._stepD.copy(this.vel).multiplyScalar(dt);
    if(current)this._stepD.addScaledVector(this._current,dt);
    const slices=Math.min(SOLID_STEP_MAX,Math.max(1,Math.ceil(this._stepD.length()/SOLID_STEP_M)));
    for(let s=0;s<slices;s++){
      this.solidPrevious.copy(this.pos);
      this.pos.addScaledVector(this._stepD,1/slices);
      this.terrainContacts.move(planet, this.solidPrevious, this.pos, this.vel, this.crouching ? 1.3 : 1.8, WALL_RADIUS);
      // Check each swept slice before the wall erases incoming momentum. Fast
      // glides/dives cannot skip the narrow catch-height window at low FPS.
      if(catchFlightLedge&&this.jet!=='none'&&this.ledgeClimb.step(0,planet,this.pos,this.vel,this.up,
        this.wish.lengthSq()>.5?this.wish:this.heading,true,false,false,true))return;
      this.resolveSettlementWalls(planet);
    }
  }

  private resolveSettlementWalls(planet: PlanetHost): void {
    // Headless tests drive a minimal planet stub.
    if (!planet.sampler || !planet.pose.invQuat) return;
    // System space → the planet's rotating local frame (meters).
    this._twTmp.copy(this.pos).sub(planet.pose.center).applyQuaternion(planet.pose.invQuat);
    const p = this._twP;
    p[0] = this._twTmp.x;
    p[1] = this._twTmp.y;
    p[2] = this._twTmp.z;
    const sc = planet.settlementColliders;
    if (sc && sc.resolve(p[0], p[1], p[2], WALL_RADIUS, this._scOut)) {
      // The colliders return the CORRECTED planet-local position; apply the delta.
      this._twTmp.set(this._scOut.x - p[0], this._scOut.y - p[1], this._scOut.z - p[2]).applyQuaternion(planet.pose.quat);
      this.pos.add(this._twTmp);
      const n = this._twTmp.normalize();
      const vn = this.vel.dot(n);
      if (vn < 0) this.vel.addScaledVector(n, -vn);
      p[0] = this._scOut.x; p[1] = this._scOut.y; p[2] = this._scOut.z;
    }
    // Scatter props: tree trunks, stumps, boulders (same corrected-position
    // contract). Trunk-only cylinders, so walking under a canopy stays free.
    if (planet.resolveScatterCollision && planet.resolveScatterCollision(p[0], p[1], p[2], WALL_RADIUS, this._scOut)) {
      this._twTmp.set(this._scOut.x - p[0], this._scOut.y - p[1], this._scOut.z - p[2]).applyQuaternion(planet.pose.quat);
      this.pos.add(this._twTmp);
      const n = this._twTmp.normalize();
      const vn = this.vel.dot(n);
      if (vn < 0) this.vel.addScaledVector(n, -vn);
      p[0] = this._scOut.x; p[1] = this._scOut.y; p[2] = this._scOut.z;
    }
    // Homestead pieces: player-built walls/pillars/rails block (same contract;
    // vertical contacts are skipped inside — floors stay the ground clamp's).
    if (planet.resolveHomesteadCollision
      && planet.resolveHomesteadCollision(p[0], p[1], p[2], WALL_RADIUS, this._scOut)) {
      this._twTmp.set(this._scOut.x - p[0], this._scOut.y - p[1], this._scOut.z - p[2]).applyQuaternion(planet.pose.quat);
      this.pos.add(this._twTmp);
      const n = this._twTmp.normalize();
      const vn = this.vel.dot(n);
      if (vn < 0) this.vel.addScaledVector(n, -vn);
    }
  }

  /** Orientation from the (up, facing) frame — avatar forward is -Z. */
  private updateQuat(): void {
    this.tmpM.lookAt(this.zero.set(0, 0, 0), this.facing, this.up);
    this.quat.setFromRotationMatrix(this.tmpM);
  }
}

/**
 * Third-person orbit camera: a boom behind the character whose length is
 * clamped by SAMPLING the analytic terrain along it — never by raycasting
 * the LOD meshes, which swap under hysteresis. Boom shortening is instant
 * (clipping is worse than a cut); re-extending eases out.
 */
const BOOM_LENGTH = 6.5;
/** Base on-foot FOV (deg). ADS divides its tangent by the weapon's adsZoom. */
const FOOT_FOV = 70;
/** Hard boom floor. ⚠ This OVERRIDES occlusion — whenever solid sits closer
 *  than BOOM_MIN behind the eye, the camera is forced inside it. 0.9 → 0.5
 *  (interiors P2 play finding): crossing a doorway puts the 0.4 m wall plane
 *  right behind the head for a beat, and the 0.9 floor parked the camera
 *  inside the door reveal ("clipping of walls around the door entries").
 *  0.5 keeps a near-shoulder frame instead; the near plane is 0.1. */
const BOOM_MIN = 0.5;
const BOOM_CLEARANCE = 0.45;
/** 8 → 24 (interiors P1): 8 samples over 6.5 m stepped ~0.81 m — a 0.4 m
 *  building-shell wall could fall BETWEEN samples and the boom landed outside
 *  the room. 24 steps ~0.27 m; the samples are analytic (terrain radius + box
 *  occlusion), so the extra cost is noise. */
const BOOM_SAMPLES = 24;
/** Indoor boom target (m): inside a building shell the default 6.5 m boom
 *  mostly rides the pull-in clamp anyway; easing toward a short frame keeps
 *  the camera from popping between clamp lengths as the player turns. */
const BOOM_INDOORS = 2.6;
/** While DIVING the boom swings overhead so the camera looks steeply DOWN the plunge (rad; positive
 *  pitch = looking down). Kept below the 1.45 look clamp so a small heading component remains — avoids
 *  the straight-down lookAt gimbal. */
const DIVE_CAM_PITCH = 1.3;

export class FootCamera {
  zoom = 1;

  /** ADS magnification of the HELD weapon (WeaponDef.adsZoom; main feeds it per
   *  frame). 1.3 ≈ the old flat 70°→56° aim tighten; a marksman scope goes well
   *  above. The FOV solves as true magnification: 2·atan(tan(base/2)/adsZoom). */
  adsZoom = 1.3;

  /** Eased 0..1 while the player is inside a building shell (interiors P1);
   *  the raw flag is fed per-frame by main via {@link setIndoors}. */
  private indoorsK = 0;
  private indoorsTarget = 0;

  private boom = BOOM_LENGTH;
  private aimK = 0;
  private glideK = 0; // eased 0..1 while gliding — pulls the camera back a bit
  private flightBoostK = 0;
  private travelShoulderSide=1;
  private diveK = 0; // eased 0..1 while diving — swings the boom overhead to look straight down
  private crouchK = 0; // eased 0..1 while crouching — lowers the eye
  private crouchMoveK = 0; // eased 0..1 while crouch-MOVING — eases the eye back up (matches the body)
  private center = new THREE.Vector3();
  private offset = new THREE.Vector3();
  private target = new THREE.Vector3();
  private simTarget = new THREE.Vector3(); // SIM-frame twin of `target` for the boom-clearance walk
  private probe = new THREE.Vector3();
  private dir = new THREE.Vector3();
  private rightV = new THREE.Vector3();
  private local = new THREE.Vector3();
  private terrainContacts = new TerrainContacts();
  private boomEnd = new THREE.Vector3();

  /** Per-frame indoors flag (interiors P1) — eased internally. */
  setIndoors(indoors: boolean): void {
    this.indoorsTarget = indoors ? 1 : 0;
  }

  /** `charPos` is the interpolated render-pose feet position (system space). */
  update(
    camera: THREE.PerspectiveCamera,
    charPos: THREE.Vector3,
    ch: CharacterController,
    planet: PlanetHost,
    origin: THREE.Vector3,
    dt: number,
  ): void {
    this.center.copy(planet.pose.center);
    this.crouchK += ((ch.crouching ? 1 : 0) - this.crouchK) * (1 - Math.exp(-10 * dt));
    this.crouchMoveK += (Math.min(1, ch.groundSpeed / 1.5) - this.crouchMoveK) * (1 - Math.exp(-8 * dt)); // smooth
    const flightBoost=ch.jet==='glide'&&!ch.onGround&&!ch.aiming
      ?ch.flightLaunchRemaining>=FLIGHT_LAUNCH_TIME||ch.divePreparation>0?.5:ch.flightPower:0;
    const wallAttached=Math.abs(ch.wallRunState)>0&&Math.abs(ch.wallRunState)<3&&!ch.onGround;
    const closeTravel=wallAttached&&!ch.aiming?1:flightBoost;
    this.flightBoostK+=(closeTravel-this.flightBoostK)*(1-Math.exp(-(closeTravel>this.flightBoostK?8:5)*dt));
    // Use boost's framing, mirrored when the wall occupies the right shoulder.
    this.travelShoulderSide+=((wallAttached&&ch.wallRunState>0?-1:1)-this.travelShoulderSide)*(1-Math.exp(-8*dt));
    // The camera FOLLOWS the interpolated feet (charPos) for smoothness, but the boom-clearance walk
    // below tests against the SIM planet pose (pose.center / surfaceRadiusWorld) — so it needs a
    // matching SIM-frame target (ch.pos). Mixing interpolated feet with the sim pose made the
    // terrain-clip FLICKER on fast-carried 10x worlds (the ~160 m/s surface shifts the feet vs. the
    // pose every sub-frame), which snapped the boom in and out. Keep the two frames separate.
    const eyeOff = EYE_HEIGHT - this.crouchK * CROUCH_CAM_DROP * (1 - 0.5 * this.crouchMoveK);
    this.target.copy(charPos).addScaledVector(ch.up, eyeOff);
    this.simTarget.copy(ch.pos).addScaledVector(ch.up, eyeOff);

    // Aiming pulls into a tight over-the-shoulder frame (standard TPS).
    this.aimK += ((ch.aiming ? 1 : 0) - this.aimK) * (1 - Math.exp(-12 * dt));
    if (this.aimK > 0.01 || this.flightBoostK > 0.001) {
      this.rightV.crossVectors(ch.heading, ch.up);
      let shift = 0.55 * this.aimK + .68*this.flightBoostK*this.travelShoulderSide*(1-this.aimK);
      // The lateral shoulder shift must clear solids too, before testing the
      // boom. Otherwise the right wall can trap its target inside the masonry.
      if(planet.pose.invQuat){
        for(let i=1;i<=8;i++){
          this.local.copy(this.simTarget).addScaledVector(this.rightV,shift*i/8).sub(this.center).applyQuaternion(planet.pose.invQuat);
          if(planet.settlementColliders?.occludes(this.local.x,this.local.y,this.local.z,BOOM_CLEARANCE)
            ||planet.homesteadOccludes?.(this.local.x,this.local.y,this.local.z,BOOM_CLEARANCE)){
            shift*=(i-1)/8;break;
          }
        }
      }
      this.target.addScaledVector(this.rightV, shift);
      this.simTarget.addScaledVector(this.rightV, shift);
    }
    // Gliding pulls the camera back a bit for a sense of speed; reverts when the
    // glide ends (jet leaves "glide").
    // Gathering is a closer aiming beat, not the wider coasting frame. Let the
    // normal glide spread begin only once the gathered burst actually fires.
    const gathering=ch.jet==='glide'&&(ch.flightLaunchRemaining>=FLIGHT_LAUNCH_TIME||ch.divePreparation>0);
    this.glideK += ((ch.jet === "glide"&&!gathering ? 1 : 0) - this.glideK) * (1 - Math.exp(-6 * dt));
    // Diving swings the boom OVERHEAD (looking straight down the plunge); eases back on release.
    this.diveK += ((ch.jet === "dive" ? 1 : ch.divePreparation) - this.diveK) * (1 - Math.exp(-8 * dt));
    // Boost-run pulls the camera back too (sense of speed); ch.boostK is already eased on the controller.
    // The dive pulls IN (≈4.7 m) so the stooping body and its folded crystals
    // stay readable; speed is sold by the field of view instead (below).
    let boomLen = (BOOM_LENGTH * this.zoom * (1 + 0.4 * this.glideK + 0.5 * ch.boostK - 0.28 * this.diveK)) * (1 - this.aimK) + 2.4 * this.aimK;
    // Thrust and wall running share an intimate shoulder frame. Existing
    // boom collision still gets final say; changing camera intent eases first.
    boomLen+=(4.4*this.zoom-boomLen)*this.flightBoostK*(1-this.aimK)*(1-this.diveK);
    // Indoors: ease toward the short interior frame (min keeps the tighter
    // aim boom when aiming inside).
    this.indoorsK += (this.indoorsTarget - this.indoorsK) * (1 - Math.exp(-8 * dt));
    boomLen += (Math.min(boomLen, BOOM_INDOORS) - boomLen) * this.indoorsK;

    // Boom direction from yaw heading + pitch. A dive biases the pitch toward straight-DOWN (DIVE_CAM_PITCH)
    // so the camera lifts overhead and follows the plunge, regardless of where the mouse was aimed.
    const camPitch = ch.pitch + (DIVE_CAM_PITCH - ch.pitch) * this.diveK;
    const cosP = Math.cos(camPitch);
    const sinP = Math.sin(camPitch);
    this.offset
      .copy(ch.heading)
      .multiplyScalar(-cosP)
      .addScaledVector(ch.up, sinP)
      .multiplyScalar(boomLen);

    // Walk the boom; stop at the last sample that clears the terrain — and
    // resident settlement building volumes (same analytic rule: never raycast).
    let maxK = 1;
    for (let i = 1; i <= BOOM_SAMPLES; i++) {
      const k = i / BOOM_SAMPLES;
      this.probe.copy(this.simTarget).addScaledVector(this.offset, k).sub(this.center);
      const r = this.probe.length() || 1;
      this.dir.copy(this.probe).divideScalar(r);
      const minR =
        planet.surfaceRadiusWorld(this.dir) + BOOM_CLEARANCE;
      if (r < minR) {
        maxK = (i - 1) / BOOM_SAMPLES;
        break;
      }
      if (planet.pose.invQuat) {
        this.local.copy(this.probe).applyQuaternion(planet.pose.invQuat);
        // Both solid-geometry fields, on the same query and the same clearance: the
        // planet's own settlement boxes, and whatever is hung on the placed-piece hook
        // (a cache instance's theme field). Missing the second one is what let the boom
        // clip through vault/hollow walls — and a low CEILING pulling the camera in is
        // the intended behaviour of the same test.
        if (planet.settlementColliders?.occludes(this.local.x, this.local.y, this.local.z, BOOM_CLEARANCE)
          || planet.homesteadOccludes?.(this.local.x, this.local.y, this.local.z, BOOM_CLEARANCE)) {
          maxK = (i - 1) / BOOM_SAMPLES;
          break;
        }
      }
    }
    this.boomEnd.copy(this.simTarget).add(this.offset);
    const cliffT = this.terrainContacts.sweep(planet, this.simTarget, this.boomEnd, BOOM_CLEARANCE);
    if (cliffT !== null) maxK = Math.min(maxK, Math.max(0, cliffT - .003 / Math.max(.001, this.offset.length())));
    // A nearby natural ceiling is allowed to shorten below the cosmetic boom
    // floor: forcing .5m here would place the camera inside that solid.
    const allowed = Math.max(cliffT === null ? BOOM_MIN : .01, this.offset.length() * maxK);
    // Shorten instantly, re-extend smoothly.
    this.boom = allowed < this.boom ? allowed : this.boom + (allowed - this.boom) * (1 - Math.exp(-5 * dt));

    this.offset.normalize().multiplyScalar(this.boom);
    camera.up.copy(ch.up);
    camera.position.copy(this.target).add(this.offset).sub(origin);
    camera.lookAt(this.probe.copy(this.target).sub(origin));

    // ADS zoom is per-WEAPON (adsZoom): true magnification, not a flat offset —
    // a 3× scope must cut the tangent, not just shave degrees.
    const zoomedFov =
      (2 * Math.atan(Math.tan((FOOT_FOV * Math.PI) / 360) / Math.max(1, this.adsZoom)) * 180) / Math.PI;
    const airRush=this.glideK*(4*Math.min(1,Math.max(0,(ch.flightSpeed-16)/34))+ch.flightPower*1.5)
      +this.diveK*10*THREE.MathUtils.smoothstep(ch.vel.length(),18,66);
    const targetFov = FOOT_FOV + (zoomedFov - FOOT_FOV) * this.aimK+airRush*(1-this.aimK);
    if (Math.abs(camera.fov - targetFov) > 0.1) {
      camera.fov += (targetFov - camera.fov) * Math.min(1, 8 * dt);
      camera.updateProjectionMatrix();
    }
  }
}
